import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
  Modal,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl, formatApiFetchError, fetchWithRetry } from '../utils/apiConfig';
import {
  exerciseByIdUrl,
  exerciseRemoveDisplayImageUrl,
  exerciseRemoveDisplayVideoUrl,
  nonCourseExerciseTypesUrl,
  exerciseTypesByCourseUrl,
  exerciseTypesByGradeUrl,
} from '../utils/exerciseApi';
import { createCourseMediaCarouselStyles } from '../components/CourseMediaCarousel';
import ModalVideoEmbed from '../components/ModalVideoEmbed';
import { ExternalBreaksCarousel } from './MyCourseDetailPage';
import { isYoutubeReconnectError } from '../utils/youtubeBinaryUpload';
import { uploadYoutubeVideoFile, startMobileWebVideoMaterialize } from '../utils/youtubeFileUpload';
import { isMobileWeb } from '../utils/youtubeUploadDebug';
import {
  createMobileWebVideoBlobUri,
  pickVideoViaShowOpenFilePicker,
  revokeMobileWebVideoBlobUri,
  validateMobileWebVideoSelection,
} from '../utils/mobileWebVideoPick';
import {
  YOUTUBE_UPLOAD_METHOD_VERSION,
  getUploadEnvironmentSnapshot,
  logYoutubeUpload,
  logYoutubeUploadError,
} from '../utils/youtubeUploadDebug';
import { getExerciseVideoPickerOptions } from '../utils/videoUploadLimits';
import YoutubeOAuthBar from '../components/YoutubeOAuthBar';
import BrandedConfirmModal from '../components/BrandedConfirmModal';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import { fetchVisibleStudents } from '../utils/fetchVisibleStudents';
import {
  canCreateNonCourseExerciseOrType,
  filterCoursesForExerciseCreation,
  validateExerciseCreationAccess,
} from '../utils/exerciseCreationAccess';
import { isGradeBeyondViewerAccess } from '../utils/gradeAccess';
import {
  getSupabaseClient,
  getExerciseImagesBucketName,
  getSupabaseConfigHint,
} from '../utils/supabase';
import { readExerciseMyOnlyFlag } from '../utils/exerciseMyOnly';

function formatExerciseSaveError(error) {
  const msg = error?.message || String(error || '');
  if (/^network error$/i.test(msg)) {
    return 'Lost connection while saving. Check your network and try again.';
  }
  if (/network|failed to fetch|load failed|timeout/i.test(msg)) {
    if (/upload|video|youtube|connection while|reach the server/i.test(msg)) {
      return msg;
    }
    return formatApiFetchError(error, getApiUrl());
  }
  return msg || 'Something went wrong. Please try again.';
}

function logExerciseSaveFailure(context, phase, error, extra = {}) {
  const err = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
  console.error('[ExerciseBuilder] save failed', {
    ts: new Date().toISOString(),
    context,
    phase,
    uploadMethodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
    env: getUploadEnvironmentSnapshot(),
    errorName: err.name,
    errorMessage: err.message,
    ...extra,
  });
  if (/upload|video|youtube/i.test(phase)) {
    logYoutubeUploadError('exercise-builder-save', phase, err, { context, ...extra });
  } else {
    logYoutubeUpload('exercise-save-error', {
      context,
      phase,
      errorName: err.name,
      errorMessage: err.message,
      ...extra,
    });
  }
}

const HEADER_IMAGE_URL = 'https://mingchuan.ie/wp-content/uploads/2016/12/cropped-head.jpg';

const EXERCISE_VIDEO_CAROUSEL_ASPECT = 16 / 9;
const EXERCISE_VIDEO_CAROUSEL_WIDTH_RATIO = 0.6;
const EXERCISE_VIDEO_EXT_THUMB_TILE_WIDTH = 92;
const EXERCISE_VIDEO_EXT_THUMB_TILE_GAP = 10;

const DEFAULT_ASSIGN_USERNAMES = ['Frank', 'Bren', 'Ciaran'];
/** Always assigned from Exercise Builder; checkbox cannot be cleared. */
const LOCKED_ASSIGN_USERNAME = 'Bren';

function isUsernameMatch(student, name) {
  return String(student?.username || '').trim().toLowerCase() === String(name).trim().toLowerCase();
}

function isLockedAssignStudent(student) {
  return isUsernameMatch(student, LOCKED_ASSIGN_USERNAME);
}

function findStudentIdByUsername(students, username) {
  const hit = (Array.isArray(students) ? students : []).find((s) => isUsernameMatch(s, username));
  return hit?.id ?? null;
}

/** Ensures Bren (when present in the roster) is always in the selection set. */
function withLockedAssignStudents(ids, students) {
  const next = studentIdsSet(ids);
  const brenId = findStudentIdByUsername(students, LOCKED_ASSIGN_USERNAME);
  if (brenId != null) next.add(normalizeStudentId(brenId));
  return next;
}

function buildDefaultAssignStudentIds(students) {
  return withLockedAssignStudents(
    (() => {
      const defaults = new Set();
      (Array.isArray(students) ? students : []).forEach((s) => {
        const u = String(s.username || '').trim().toLowerCase();
        if (DEFAULT_ASSIGN_USERNAMES.some((n) => n.toLowerCase() === u)) {
          const sid = normalizeStudentId(s.id);
          if (sid != null) defaults.add(sid);
        }
      });
      return defaults;
    })(),
    students
  );
}

function studentDisplayName(student) {
  const name = String(student?.student_name || '').trim();
  const username = String(student?.username || '').trim();
  if (name) return name;
  if (username) return username;
  return `Student ${student?.id ?? ''}`;
}

/** List label — omit username when it duplicates the display name (e.g. avoid "Frank (Frank)"). */
function studentAssignListLabel(student, { locked = false } = {}) {
  const name = String(student?.student_name || '').trim();
  const username = String(student?.username || '').trim();
  const display = studentDisplayName(student);
  let label = display;
  if (username && name && name.localeCompare(username, undefined, { sensitivity: 'base' }) !== 0) {
    label = `${display} (${username})`;
  }
  if (locked) label += ' — required';
  return label;
}

function normalizeStudentId(id) {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isNaN(n) ? null : n;
}

function studentIdsSet(raw) {
  const set = new Set();
  const list =
    raw instanceof Set ? [...raw] : Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  list.forEach((id) => {
    const n = normalizeStudentId(id);
    if (n != null) set.add(n);
  });
  return set;
}

function isStudentIdSelected(selectedSet, studentId) {
  const n = normalizeStudentId(studentId);
  return n != null && selectedSet.has(n);
}

function sanitizeExerciseFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'image').slice(0, 180);
}

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'image/jpeg';
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read image (${res.status})`);
  return res.arrayBuffer();
}

const NEW_EXERCISE_ORDER_ID = 'new';

const EDIT_TOAST_SUCCESS_MESSAGE = 'exercise has been successfully updated.';
const EDIT_TOAST_FAILURE_MESSAGE = 'the exercise has not been updated';

function sortExercisesByOrder(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const oa = a.exercise_order ?? 0;
    const ob = b.exercise_order ?? 0;
    if (oa !== ob) return oa - ob;
    return (a.id ?? 0) - (b.id ?? 0);
  });
}

/** Existing type exercises + current (unsaved) exercise at end; user reorders before save. */
function buildExerciseOrderList(existingExercises, currentExerciseName) {
  const sorted = sortExercisesByOrder(existingExercises);
  const name = String(currentExerciseName || '').trim() || 'New exercise';
  const list = sorted.map((e) => ({
    id: e.id,
    exercise_name: e.exercise_name,
  }));
  list.push({ id: NEW_EXERCISE_ORDER_ID, exercise_name: name });
  return list;
}

function buildExerciseOrderListForEdit(existingExercises) {
  return sortExercisesByOrder(existingExercises).map((e) => ({
    id: e.id,
    exercise_name: e.exercise_name,
  }));
}

function webDataSet(attrs) {
  return Platform.OS === 'web' ? { dataSet: attrs } : {};
}

function MovePickDropdown({
  styles,
  triggerText,
  isPlaceholder,
  disabled,
  open,
  onToggle,
  hasArrow,
  children,
}) {
  return (
    <View style={styles.moveDestDropdownWrap} {...webDataSet({ exerciseBuilderMoveDest: 'true' })}>
      <TouchableOpacity
        style={[styles.pickTrigger, disabled && styles.pickTriggerDisabled]}
        onPress={onToggle}
        disabled={disabled}
        {...webDataSet({ dropdownTrigger: 'true' })}
      >
        <Text
          style={[styles.pickTriggerText, isPlaceholder && styles.pickTriggerPlaceholder]}
          numberOfLines={2}
          {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
        >
          {triggerText}
        </Text>
        {hasArrow ? (
          <Text
            style={styles.pickTriggerArrow}
            {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
          >
            {open ? '▲' : '▼'}
          </Text>
        ) : null}
      </TouchableOpacity>
      {open ? (
        <View
          style={styles.moveTypeDropdownList}
          {...webDataSet({ dropdownSurface: 'true', exerciseBuilderMoveDestList: 'true' })}
        >
          {children}
        </View>
      ) : null}
    </View>
  );
}

function MovePickOptions({
  styles,
  items,
  selectedId,
  currentId,
  currentSuffix,
  getName,
  onSelect,
  emptyText,
  loading = false,
  loadingText = 'Loading…',
}) {
  if (loading) {
    return (
      <Text
        style={[styles.dropdownOptionText, styles.moveDestEmptyText]}
        {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
      >
        {loadingText}
      </Text>
    );
  }
  if (!items || items.length === 0) {
    return (
      <Text
        style={[styles.dropdownOptionText, styles.moveDestEmptyText]}
        {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
      >
        {emptyText}
      </Text>
    );
  }
  return (
    <ScrollView
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      style={styles.moveDestOptionsScroll}
    >
      {items.map((item) => {
        const isCurrent = currentId != null && item.id === currentId;
        const isSelected = !isCurrent && selectedId != null && item.id === selectedId;
        const name = getName(item);
        return (
          <TouchableOpacity
            key={item.id}
            style={[
              styles.dropdownOption,
              isSelected && styles.dropdownOptionSelected,
              isCurrent && styles.dropdownOptionDisabled,
            ]}
            onPress={() => onSelect(item)}
            disabled={isCurrent}
            activeOpacity={isCurrent ? 1 : 0.7}
            {...webDataSet({
              dropdownRow: 'true',
              exerciseBuilderMoveOptionDisabled: isCurrent ? 'true' : 'false',
            })}
          >
            <Text
              style={[
                styles.dropdownOptionText,
                isSelected && styles.dropdownOptionTextSelected,
                isCurrent && styles.dropdownOptionTextDisabled,
              ]}
              {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
            >
              {isCurrent ? `${name} (${currentSuffix})` : name}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}

function parseDisplayImagesCsv(csv) {
  if (!csv || typeof csv !== 'string') return [];
  return csv.split(',').map((s) => s.trim()).filter((u) => /^https?:\/\//i.test(u));
}

function isYouTubeUrl(url) {
  return /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/i.test(String(url || '').trim());
}

function normalizeMediaUrl(url) {
  try {
    return new URL(String(url).trim()).href.replace(/\/+$/, '');
  } catch {
    return String(url || '').trim().replace(/\/+$/, '');
  }
}

function isLikelyImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (isYouTubeUrl(url)) return false;
  const noQuery = url.split('?')[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|mpeg|mpg)$/i.test(noQuery)) return false;
  const path = url.split('?')[0].toLowerCase();
  if (path.includes('/assets/')) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(path.trim());
}

export default function ExerciseBuilder({ navigation, route }) {
  const theme = useTheme();
  const [currentUser, setCurrentUser] = useState(null);
  const stopwatchRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [gradeExerciseTypes, setGradeExerciseTypes] = useState([]);
  const [nonCourseExerciseTypes, setNonCourseExerciseTypes] = useState([]);
  /** @type {'course' | 'grade' | 'nonCourse' | null} */
  const [typeSelectionSource, setTypeSelectionSource] = useState(null);
  const [selectedGradeId, setSelectedGradeId] = useState(null);
  const [loadingGradeExerciseTypes, setLoadingGradeExerciseTypes] = useState(false);
  const [loadingNonCourseTypes, setLoadingNonCourseTypes] = useState(false);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showCourseTypesDropdown, setShowCourseTypesDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [showGradeTypesDropdown, setShowGradeTypesDropdown] = useState(false);
  const [showNonCourseTypesDropdown, setShowNonCourseTypesDropdown] = useState(false);
  
  // Form state
  const [exerciseName, setExerciseName] = useState('');
  const [sets, setSets] = useState('1');
  const [reps, setReps] = useState('1');
  const [isTimeBasedExercise, setIsTimeBasedExercise] = useState(false);
  const [isHeightBased, setIsHeightBased] = useState(false);
  const [isWeightBased, setIsWeightBased] = useState(false);
  const [exerciseTime, setExerciseTime] = useState('30');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedExerciseTypeId, setSelectedExerciseTypeId] = useState(null);
  const [description, setDescription] = useState('');
  const [isSequenceExercise, setIsSequenceExercise] = useState(false);
  const [exerciseOrderList, setExerciseOrderList] = useState([]);
  const [loadingExerciseOrder, setLoadingExerciseOrder] = useState(false);
  /** 'upload' = device gallery → YouTube; 'url' = paste Display URL (option 2). */
  const [videoSourceMode, setVideoSourceMode] = useState('upload');
  const [displayUrl, setDisplayUrl] = useState('');
  const [pendingVideo, setPendingVideo] = useState(null);
  const [videoPickError, setVideoPickError] = useState('');
  const [showFilesFallback, setShowFilesFallback] = useState(false);
  const webVideoFileRef = useRef(null);
  const webVideoBlobUriRef = useRef(null);
  const webVideoFileInputRef = useRef(null);
  const youtubeOAuthRef = useRef(null);
  const [pendingImages, setPendingImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [builderTab, setBuilderTab] = useState('new');
  const [editCourseId, setEditCourseId] = useState(null);
  const [editExerciseTypes, setEditExerciseTypes] = useState([]);
  const [editGradeId, setEditGradeId] = useState(null);
  const [editGradeExerciseTypes, setEditGradeExerciseTypes] = useState([]);
  const [editNonCourseExerciseTypes, setEditNonCourseExerciseTypes] = useState([]);
  /** @type {'course' | 'grade' | 'nonCourse' | null} */
  const [editSelectionSource, setEditSelectionSource] = useState(null);
  const [editExerciseTypeId, setEditExerciseTypeId] = useState(null);
  const [loadingEditGradeExerciseTypes, setLoadingEditGradeExerciseTypes] = useState(false);
  const [loadingEditNonCourseTypes, setLoadingEditNonCourseTypes] = useState(false);
  const [showEditCourseDropdown, setShowEditCourseDropdown] = useState(false);
  const [showEditCourseTypesDropdown, setShowEditCourseTypesDropdown] = useState(false);
  const [showEditGradeDropdown, setShowEditGradeDropdown] = useState(false);
  const [showEditGradeTypesDropdown, setShowEditGradeTypesDropdown] = useState(false);
  const [showEditNonCourseTypesDropdown, setShowEditNonCourseTypesDropdown] = useState(false);
  const [editExercises, setEditExercises] = useState([]);
  const [loadingEditExercises, setLoadingEditExercises] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [existingDisplayImages, setExistingDisplayImages] = useState([]);
  /** Image URLs stored in legacy `display_url` (not `display_images`). */
  const [displayUrlOnlyImages, setDisplayUrlOnlyImages] = useState([]);
  const [existingDisplayVideoUrl, setExistingDisplayVideoUrl] = useState(null);
  const [removingDisplayImageUrl, setRemovingDisplayImageUrl] = useState(null);
  const [removingDisplayVideo, setRemovingDisplayVideo] = useState(false);
  /** @type {{ kind: 'image' | 'video', url?: string, viaDisplayUrl?: boolean } | null} */
  const [removeMediaConfirm, setRemoveMediaConfirm] = useState(null);
  const removeMediaPendingRef = useRef(null);
  const [openExerciseDeleteMenuId, setOpenExerciseDeleteMenuId] = useState(null);
  const [exerciseDeleteTarget, setExerciseDeleteTarget] = useState(null);
  const [exerciseDeleteTypedValue, setExerciseDeleteTypedValue] = useState('');
  const [deletingExercise, setDeletingExercise] = useState(false);
  const [exerciseDeleteError, setExerciseDeleteError] = useState('');
  const exerciseDeleteGenRef = useRef(0);
  const [exerciseVideoModal, setExerciseVideoModal] = useState(null);
  const [moveExerciseMode, setMoveExerciseMode] = useState(false);
  const [moveSelectedExerciseIds, setMoveSelectedExerciseIds] = useState(() => new Set());
  const [moveTargetTypeId, setMoveTargetTypeId] = useState(null);
  const [moveDestKind, setMoveDestKind] = useState(null);
  const [showMoveTypeDropdown, setShowMoveTypeDropdown] = useState(false);
  const [moveDestCourseId, setMoveDestCourseId] = useState(null);
  const [moveDestCourseTypes, setMoveDestCourseTypes] = useState([]);
  const [loadingMoveDestCourseTypes, setLoadingMoveDestCourseTypes] = useState(false);
  const [showMoveDestCourseDropdown, setShowMoveDestCourseDropdown] = useState(false);
  const [showMoveDestCourseTypeDropdown, setShowMoveDestCourseTypeDropdown] = useState(false);
  const [moveDestGradeId, setMoveDestGradeId] = useState(null);
  const [moveDestGradeTypes, setMoveDestGradeTypes] = useState([]);
  const [loadingMoveDestGradeTypes, setLoadingMoveDestGradeTypes] = useState(false);
  const [showMoveDestGradeDropdown, setShowMoveDestGradeDropdown] = useState(false);
  const [showMoveDestGradeTypeDropdown, setShowMoveDestGradeTypeDropdown] = useState(false);
  const [showMoveDestNonCourseDropdown, setShowMoveDestNonCourseDropdown] = useState(false);
  const [movingExercises, setMovingExercises] = useState(false);
  const moveDestCourseFetchGenRef = useRef(0);
  const moveDestGradeFetchGenRef = useRef(0);
  const [editUpdateToast, setEditUpdateToast] = useState(null);
  const [assignStudents, setAssignStudents] = useState([]);
  const [selectedAssignStudentIds, setSelectedAssignStudentIds] = useState(() => new Set());
  const [myExerciseOnly, setMyExerciseOnly] = useState(false);
  const [loadingAssignStudents, setLoadingAssignStudents] = useState(false);
  const [showAssignStudentsDropdown, setShowAssignStudentsDropdown] = useState(false);

  useEffect(() => {
    const userId = route?.params?.userId || 1;
    fetchUser(userId);
    fetchCourses();
    fetchGrades();
    void loadAssignStudents();
  }, []);

  useEffect(() => {
    if (currentUser?.id == null) return;
    void fetchNonCourseExerciseTypes();
    void fetchEditNonCourseExerciseTypes();
  }, [currentUser?.id]);

  // Refresh course-scoped exercise types when returning from Exercise Type Builder
  useFocusEffect(
    React.useCallback(() => {
      if (builderTab === 'new' && selectedCourseId) {
        fetchExerciseTypesForCourse(selectedCourseId);
      }
      if (builderTab === 'new' && selectedGradeId) {
        void fetchGradeExerciseTypes(selectedGradeId);
      }
      if (builderTab === 'edit' && editCourseId) {
        fetchEditExerciseTypesForCourse(editCourseId);
      }
      if (builderTab === 'edit' && editGradeId) {
        void fetchEditGradeExerciseTypes(editGradeId);
      }
      void fetchNonCourseExerciseTypes();
      void fetchEditNonCourseExerciseTypes();
      void youtubeOAuthRef.current?.refresh?.();
    }, [builderTab, selectedCourseId, selectedGradeId, editCourseId, editGradeId, currentUser?.id])
  );

  useEffect(() => {
    if (videoSourceMode === 'upload') {
      void youtubeOAuthRef.current?.refresh?.();
    }
  }, [videoSourceMode]);

  const fetchUser = async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        setGrades(
          (Array.isArray(data) ? data : []).sort(
            (a, b) => (a.grade_rank_order ?? 0) - (b.grade_rank_order ?? 0)
          )
        );
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchNonCourseExerciseTypes = async () => {
    const viewerId = currentUser?.id;
    if (viewerId == null) {
      setNonCourseExerciseTypes([]);
      return;
    }
    setLoadingNonCourseTypes(true);
    try {
      const response = await fetch(nonCourseExerciseTypesUrl({ viewerId }));
      if (response.ok) {
        const data = await response.json();
        setNonCourseExerciseTypes(Array.isArray(data) ? data : []);
      } else {
        setNonCourseExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching non-course exercise types:', error);
      setNonCourseExerciseTypes([]);
    } finally {
      setLoadingNonCourseTypes(false);
    }
  };

  const fetchEditNonCourseExerciseTypes = async () => {
    const viewerId = currentUser?.id;
    if (viewerId == null) {
      setEditNonCourseExerciseTypes([]);
      return;
    }
    setLoadingEditNonCourseTypes(true);
    try {
      const response = await fetch(nonCourseExerciseTypesUrl({ viewerId }));
      if (response.ok) {
        const data = await response.json();
        setEditNonCourseExerciseTypes(Array.isArray(data) ? data : []);
      } else {
        setEditNonCourseExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching non-course exercise types for edit:', error);
      setEditNonCourseExerciseTypes([]);
    } finally {
      setLoadingEditNonCourseTypes(false);
    }
  };

  const fetchGradeExerciseTypes = async (gradeId) => {
    if (gradeId == null) {
      setGradeExerciseTypes([]);
      return;
    }
    setLoadingGradeExerciseTypes(true);
    try {
      const response = await fetch(exerciseTypesByGradeUrl(gradeId));
      if (response.ok) {
        const data = await response.json();
        setGradeExerciseTypes(Array.isArray(data) ? data : []);
      } else {
        setGradeExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching grade exercise types:', error);
      setGradeExerciseTypes([]);
    } finally {
      setLoadingGradeExerciseTypes(false);
    }
  };

  const fetchEditGradeExerciseTypes = async (gradeId) => {
    if (gradeId == null) {
      setEditGradeExerciseTypes([]);
      return;
    }
    setLoadingEditGradeExerciseTypes(true);
    try {
      const response = await fetch(exerciseTypesByGradeUrl(gradeId));
      if (response.ok) {
        const data = await response.json();
        setEditGradeExerciseTypes(Array.isArray(data) ? data : []);
      } else {
        setEditGradeExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching grade exercise types for edit:', error);
      setEditGradeExerciseTypes([]);
    } finally {
      setLoadingEditGradeExerciseTypes(false);
    }
  };

  const buildMyExerciseOnlyAssignIds = useCallback(() => {
    const creatorId = normalizeStudentId(currentUser?.id);
    return creatorId != null ? studentIdsSet([creatorId]) : new Set();
  }, [currentUser?.id]);

  const loadAssignStudents = async () => {
    setLoadingAssignStudents(true);
    try {
      const { students: rows } = await fetchVisibleStudents({ requester: currentUser });
      const list = Array.isArray(rows) ? rows : [];
      setAssignStudents(list);
      setSelectedAssignStudentIds((prev) => {
        if (myExerciseOnly) return buildMyExerciseOnlyAssignIds();
        return withLockedAssignStudents(
          prev.size > 0 ? prev : buildDefaultAssignStudentIds(list),
          list
        );
      });
    } catch (error) {
      console.error('Error loading students for assignment:', error);
      setAssignStudents([]);
    } finally {
      setLoadingAssignStudents(false);
    }
  };

  const toggleAssignStudent = (student) => {
    if (myExerciseOnly) return;
    if (isLockedAssignStudent(student)) return;
    const studentId = normalizeStudentId(student.id);
    if (studentId == null) return;
    setSelectedAssignStudentIds((prev) => {
      const next = studentIdsSet(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return withLockedAssignStudents(next, assignStudents);
    });
  };

  const toggleMyExerciseOnly = () => {
    setMyExerciseOnly((prev) => {
      const next = !prev;
      if (next) {
        setSelectedAssignStudentIds(buildMyExerciseOnlyAssignIds());
      } else {
        setSelectedAssignStudentIds(buildDefaultAssignStudentIds(assignStudents));
      }
      return next;
    });
  };

  const assignExerciseToStudents = async (exerciseId, studentIds, { myExerciseOnlyMode = false } = {}) => {
    const ids = myExerciseOnlyMode
      ? [...buildMyExerciseOnlyAssignIds()]
      : [...withLockedAssignStudents(studentIds, assignStudents)].filter((id) => id != null);
    if (ids.length === 0) return;

    const assignerId = currentUser?.id ?? null;
    const notes = assignerId
      ? `Assigned from Exercise Builder by ${currentUser?.student_name || currentUser?.username || 'user'}`
      : 'Assigned from Exercise Builder';

    const viewerQuery =
      assignerId != null
        ? `&viewer_id=${encodeURIComponent(String(assignerId))}`
        : '';
    let existingRows = [];
    try {
      const listRes = await fetch(
        `${getApiUrl()}/api/student-exercise-assignments?exercise_id=${encodeURIComponent(String(exerciseId))}&active=true${viewerQuery}`
      );
      if (listRes.ok) {
        const parsed = await listRes.json();
        existingRows = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.error('Error loading assignments for sync:', error);
    }

    const targetIdSet = studentIdsSet(ids);
    const deactivateRequests = (Array.isArray(existingRows) ? existingRows : [])
      .filter((row) => {
        const sid = normalizeStudentId(row.student_id);
        return sid != null && !targetIdSet.has(sid) && row.id != null;
      })
      .map((row) =>
        fetch(`${getApiUrl()}/api/student-exercise-assignments/${encodeURIComponent(String(row.id))}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: false }),
        })
      );

    const activateRequests = ids.map((studentId) =>
      fetch(`${getApiUrl()}/api/student-exercise-assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          exercise_id: exerciseId,
          assigned_by: assignerId,
          active: true,
          notes,
        }),
      })
    );

    const results = await Promise.all([...deactivateRequests, ...activateRequests]);
    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      throw new Error(
        `Exercise saved but failed to assign to ${failed.length} student(s). Please try Assignments.`
      );
    }
  };

  const loadAssignSelectionsForExercise = useCallback(
    async (exerciseId, opts = {}) => {
      const isMyOnly = opts.isMyExerciseOnly === true || readExerciseMyOnlyFlag({ isMyExerciseOnly: opts.isMyExerciseOnly });

      const applySelection = (ids, myOnly) => {
        if (myOnly) {
          setMyExerciseOnly(true);
          setSelectedAssignStudentIds(buildMyExerciseOnlyAssignIds());
          return;
        }
        setMyExerciseOnly(false);
        setSelectedAssignStudentIds(withLockedAssignStudents(ids, assignStudents));
      };

      if (exerciseId == null) {
        applySelection(buildDefaultAssignStudentIds(assignStudents), false);
        return;
      }
      if (isMyOnly) {
        applySelection(buildMyExerciseOnlyAssignIds(), true);
        return;
      }
      try {
        const viewerQuery =
          currentUser?.id != null
            ? `&viewer_id=${encodeURIComponent(String(currentUser.id))}`
            : '';
        const res = await fetch(
          `${getApiUrl()}/api/student-exercise-assignments?exercise_id=${encodeURIComponent(String(exerciseId))}&active=true${viewerQuery}`
        );
        if (res.ok) {
          const rows = await res.json();
          const ids = studentIdsSet(
            (Array.isArray(rows) ? rows : [])
              .map((r) => r.student_id)
              .filter((id) => id != null)
          );
          applySelection(
            ids.size > 0 ? ids : buildDefaultAssignStudentIds(assignStudents),
            false
          );
          return;
        }
      } catch (error) {
        console.error('Error loading exercise assignments:', error);
      }
      applySelection(buildDefaultAssignStudentIds(assignStudents), false);
    },
    [assignStudents, buildMyExerciseOnlyAssignIds, currentUser?.id]
  );

  useEffect(() => {
    if (editingExerciseId == null || assignStudents.length === 0 || myExerciseOnly) return;
    void loadAssignSelectionsForExercise(editingExerciseId);
  }, [editingExerciseId, assignStudents.length, myExerciseOnly, loadAssignSelectionsForExercise]);

  const selectedAssignStudentsSummary = (() => {
    if (myExerciseOnly) return 'My Exercise only';
    if (selectedAssignStudentIds.size === 0) return '';
    const names = assignStudents
      .filter((s) => isStudentIdSelected(selectedAssignStudentIds, s.id))
      .map((s) => studentDisplayName(s));
    return names.join(', ');
  })();

  const assignStudentIdsInList = useMemo(
    () =>
      assignStudents
        .map((student) => normalizeStudentId(student.id))
        .filter((id) => id != null),
    [assignStudents]
  );

  const allAssignStudentsSelected = useMemo(() => {
    if (assignStudentIdsInList.length === 0) return false;
    return assignStudentIdsInList.every((id) => isStudentIdSelected(selectedAssignStudentIds, id));
  }, [assignStudentIdsInList, selectedAssignStudentIds]);

  const handleSelectAllAssignStudentsToggle = () => {
    if (myExerciseOnly) return;
    if (allAssignStudentsSelected) {
      setSelectedAssignStudentIds(withLockedAssignStudents(new Set(), assignStudents));
      return;
    }
    setSelectedAssignStudentIds(
      withLockedAssignStudents(studentIdsSet(assignStudentIdsInList), assignStudents)
    );
  };

  const fetchExerciseTypesForCourse = async (courseId) => {
    if (courseId == null) {
      setExerciseTypes([]);
      return;
    }
    try {
      const response = await fetch(
        `${getApiUrl()}/api/exercise-types/course/${courseId}`
      );
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(data);
      } else {
        setExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching exercise types for course:', error);
      setExerciseTypes([]);
    }
  };

  const fetchEditExerciseTypesForCourse = async (courseId) => {
    if (courseId == null) {
      setEditExerciseTypes([]);
      return;
    }
    try {
      const response = await fetch(
        `${getApiUrl()}/api/exercise-types/course/${courseId}`
      );
      if (response.ok) {
        setEditExerciseTypes(await response.json());
      } else {
        setEditExerciseTypes([]);
      }
    } catch (error) {
      console.error('Error fetching exercise types for edit:', error);
      setEditExerciseTypes([]);
    }
  };

  const fetchEditExercisesForType = async (exerciseTypeId) => {
    if (exerciseTypeId == null) {
      setEditExercises([]);
      return;
    }
    setLoadingEditExercises(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(exerciseTypeId))}`
      );
      const data = response.ok ? await response.json() : [];
      setEditExercises(sortExercisesByOrder(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error fetching exercises for edit:', error);
      setEditExercises([]);
    } finally {
      setLoadingEditExercises(false);
    }
  };

  const clearNewGradeSelection = () => {
    setSelectedGradeId(null);
    setGradeExerciseTypes([]);
    setShowGradeDropdown(false);
    setShowGradeTypesDropdown(false);
  };

  const clearNewCourseSelection = () => {
    setSelectedCourseId(null);
    setExerciseTypes([]);
    setShowCourseDropdown(false);
    setShowCourseTypesDropdown(false);
  };

  const clearNewExerciseTypeSelection = () => {
    setSelectedExerciseTypeId(null);
    setTypeSelectionSource(null);
  };

  const clearEditGradeSelection = () => {
    setEditGradeId(null);
    setEditGradeExerciseTypes([]);
    setShowEditGradeDropdown(false);
    setShowEditGradeTypesDropdown(false);
  };

  const clearEditNonCourseSelection = () => {
    setShowEditNonCourseTypesDropdown(false);
  };

  const handleCourseSelect = (course) => {
    if (selectedCourseId === course.id && typeSelectionSource === 'course') {
      clearNewCourseSelection();
      clearNewExerciseTypeSelection();
      return;
    }
    clearNewGradeSelection();
    setShowNonCourseTypesDropdown(false);
    clearNewExerciseTypeSelection();
    setSelectedCourseId(course.id);
    setTypeSelectionSource('course');
    setShowCourseDropdown(false);
    setShowCourseTypesDropdown(false);
    fetchExerciseTypesForCourse(course.id);
  };

  const handleNewGradeSelect = (grade) => {
    if (isGradeBeyondViewerAccess(grade, currentUser)) return;
    if (selectedGradeId === grade.id && typeSelectionSource === 'grade') {
      clearNewGradeSelection();
      clearNewExerciseTypeSelection();
      return;
    }
    clearNewCourseSelection();
    setShowNonCourseTypesDropdown(false);
    clearNewExerciseTypeSelection();
    setSelectedGradeId(grade.id);
    setShowGradeDropdown(false);
    setShowGradeTypesDropdown(false);
    void fetchGradeExerciseTypes(grade.id);
  };

  const handleNewCourseExerciseTypeSelect = (exerciseType) => {
    setTypeSelectionSource('course');
    setSelectedExerciseTypeId(exerciseType.id);
    setShowCourseTypesDropdown(false);
  };

  const handleNewGradeExerciseTypeSelect = (exerciseType) => {
    setTypeSelectionSource('grade');
    setSelectedExerciseTypeId(exerciseType.id);
    setShowGradeTypesDropdown(false);
  };

  const handleNewNonCourseExerciseTypeSelect = (exerciseType) => {
    clearNewCourseSelection();
    clearNewGradeSelection();
    setTypeSelectionSource('nonCourse');
    setSelectedExerciseTypeId(exerciseType.id);
    setShowNonCourseTypesDropdown(false);
  };

  const loadExerciseOrderList = useCallback(
    async (exerciseTypeId, currentName, { forEdit = false } = {}) => {
      if (exerciseTypeId == null) {
        setExerciseOrderList([]);
        return;
      }
      setLoadingExerciseOrder(true);
      try {
        const res = await fetch(
          `${getApiUrl()}/api/exercises?exercise_type_id=${encodeURIComponent(String(exerciseTypeId))}`
        );
        const data = res.ok ? await res.json() : [];
        setExerciseOrderList(
          forEdit
            ? buildExerciseOrderListForEdit(data)
            : buildExerciseOrderList(data, currentName)
        );
      } catch (error) {
        console.error('Error loading exercises for order:', error);
        setExerciseOrderList(
          forEdit ? [] : buildExerciseOrderList([], currentName)
        );
      } finally {
        setLoadingExerciseOrder(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isSequenceExercise) {
      setExerciseOrderList([]);
      return;
    }
    if (selectedExerciseTypeId == null) {
      setExerciseOrderList([]);
      return;
    }
    const forEdit = builderTab === 'edit' && editingExerciseId != null;
    void loadExerciseOrderList(selectedExerciseTypeId, exerciseName, { forEdit });
  }, [
    isSequenceExercise,
    selectedExerciseTypeId,
    loadExerciseOrderList,
    builderTab,
    editingExerciseId,
  ]);

  useEffect(() => {
    if (!isSequenceExercise) return;
    const name = exerciseName.trim() || (builderTab === 'edit' ? 'Exercise' : 'New exercise');
    const trackId =
      builderTab === 'edit' && editingExerciseId != null
        ? editingExerciseId
        : NEW_EXERCISE_ORDER_ID;
    setExerciseOrderList((prev) => {
      if (!prev.some((item) => item.id === trackId)) return prev;
      return prev.map((item) =>
        item.id === trackId ? { ...item, exercise_name: name } : item
      );
    });
  }, [exerciseName, isSequenceExercise, builderTab, editingExerciseId]);

  const handleMoveExerciseOrder = (index, direction) => {
    setExerciseOrderList((prev) => {
      const next = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= next.length) return prev;
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  };

  const persistExerciseOrders = async (orderList, resolveNewId = null) => {
    const resolved = orderList.map((item) =>
      item.id === NEW_EXERCISE_ORDER_ID && resolveNewId != null
        ? { ...item, id: resolveNewId }
        : item
    );
    for (let i = 0; i < resolved.length; i += 1) {
      const item = resolved[i];
      const orderValue = i + 1;
      const putRes = await fetch(
        `${getApiUrl()}/api/exercises/${encodeURIComponent(String(item.id))}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            exercise_name: null,
            sets: null,
            reps: null,
            exercise_type_id: null,
            description: null,
            display_url: null,
            exercise_order: orderValue,
            display_images: null,
          }),
        }
      );
      const putRaw = await putRes.text();
      let putBody = {};
      try {
        putBody = putRaw ? JSON.parse(putRaw) : {};
      } catch {
        /* ignore */
      }
      if (!putRes.ok) {
        throw new Error(putBody.error || `Failed to save exercise order for "${item.exercise_name}"`);
      }
    }
  };

  const resetFormFieldsOnly = () => {
    setExerciseName('');
    setSets('1');
    setReps('1');
    setIsTimeBasedExercise(false);
    setIsHeightBased(false);
    setIsWeightBased(false);
    setExerciseTime('30');
    setDescription('');
    setIsSequenceExercise(false);
    setExerciseOrderList([]);
    setLoadingExerciseOrder(false);
    setPendingImages([]);
    setExistingDisplayImages([]);
    setDisplayUrlOnlyImages([]);
    setExistingDisplayVideoUrl(null);
    setRemoveMediaConfirm(null);
    removeMediaPendingRef.current = null;
    setVideoSourceMode('upload');
    setDisplayUrl('');
    setPendingVideo(null);
    webVideoFileRef.current = null;
    setVideoPickError('');
    setShowAssignStudentsDropdown(false);
    setMyExerciseOnly(false);
    setSelectedAssignStudentIds(buildDefaultAssignStudentIds(assignStudents));
  };

  const handleReset = () => {
    resetFormFieldsOnly();
    clearNewCourseSelection();
    clearNewGradeSelection();
    clearNewExerciseTypeSelection();
    setShowNonCourseTypesDropdown(false);
    setSaveStatus(null);
    setErrorMessage('');
  };

  const showEditUpdateToast = (type, message) => {
    setEditUpdateToast({
      type,
      message:
        message ??
        (type === 'success' ? EDIT_TOAST_SUCCESS_MESSAGE : EDIT_TOAST_FAILURE_MESSAGE),
    });
  };

  const closeAllMoveDropdowns = () => {
    setShowMoveTypeDropdown(false);
    setShowMoveDestCourseDropdown(false);
    setShowMoveDestCourseTypeDropdown(false);
    setShowMoveDestGradeDropdown(false);
    setShowMoveDestGradeTypeDropdown(false);
    setShowMoveDestNonCourseDropdown(false);
  };

  const resetMoveDestinationSelections = () => {
    moveDestCourseFetchGenRef.current += 1;
    moveDestGradeFetchGenRef.current += 1;
    setMoveTargetTypeId(null);
    setMoveDestKind(null);
    setMoveDestCourseId(null);
    setMoveDestCourseTypes([]);
    setLoadingMoveDestCourseTypes(false);
    setMoveDestGradeId(null);
    setMoveDestGradeTypes([]);
    setLoadingMoveDestGradeTypes(false);
    closeAllMoveDropdowns();
  };

  const resetMoveExerciseState = () => {
    setMoveExerciseMode(false);
    setMoveSelectedExerciseIds(new Set());
    setMovingExercises(false);
    resetMoveDestinationSelections();
  };

  const handleMoveExerciseModeToggle = (enabled) => {
    setMoveExerciseMode(enabled);
    setMoveSelectedExerciseIds(new Set());
    resetMoveDestinationSelections();
    setOpenExerciseDeleteMenuId(null);
  };

  const openExerciseDeleteConfirm = (exercise) => {
    setOpenExerciseDeleteMenuId(null);
    setExerciseDeleteError('');
    setExerciseDeleteTypedValue('');
    setExerciseDeleteTarget({
      id: exercise.id,
      name: exercise.exercise_name || 'this exercise',
    });
  };

  const dismissExerciseDeleteConfirm = () => {
    if (deletingExercise) return;
    exerciseDeleteGenRef.current += 1;
    setExerciseDeleteTarget(null);
    setExerciseDeleteTypedValue('');
    setExerciseDeleteError('');
  };

  const handleDeleteExerciseConfirm = async () => {
    if (!exerciseDeleteTarget?.id) return;
    if (String(exerciseDeleteTypedValue).trim().toLowerCase() !== 'delete') return;
    const gen = ++exerciseDeleteGenRef.current;
    setDeletingExercise(true);
    setExerciseDeleteError('');
    try {
      const res = await fetch(
        `${getApiUrl()}/api/exercises/${encodeURIComponent(String(exerciseDeleteTarget.id))}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete exercise.');
      }
      if (gen !== exerciseDeleteGenRef.current) return;
      const deletedId = exerciseDeleteTarget.id;
      setExerciseDeleteTarget(null);
      setExerciseDeleteTypedValue('');
      if (editingExerciseId === deletedId) {
        setEditingExerciseId(null);
        resetFormFieldsOnly();
      }
      showEditUpdateToast('success', 'Exercise deleted successfully.');
      if (editExerciseTypeId != null) {
        await fetchEditExercisesForType(editExerciseTypeId);
      }
    } catch (error) {
      if (gen !== exerciseDeleteGenRef.current) return;
      console.error('Error deleting exercise:', error);
      setExerciseDeleteError(error.message || 'Failed to delete exercise.');
    } finally {
      if (gen === exerciseDeleteGenRef.current) {
        setDeletingExercise(false);
      }
    }
  };

  const handleMoveExerciseListPress = (exercise) => {
    if (!moveExerciseMode) {
      handleEditExerciseSelect(exercise);
      return;
    }
    setMoveSelectedExerciseIds((prev) => {
      const next = new Set(prev);
      if (next.has(exercise.id)) {
        next.delete(exercise.id);
      } else {
        next.add(exercise.id);
      }
      return next;
    });
    resetMoveDestinationSelections();
  };

  const toggleMoveDropdown = (key) => {
    const currentlyOpen = {
      same: showMoveTypeDropdown,
      course: showMoveDestCourseDropdown,
      courseType: showMoveDestCourseTypeDropdown,
      grade: showMoveDestGradeDropdown,
      gradeType: showMoveDestGradeTypeDropdown,
      nonCourse: showMoveDestNonCourseDropdown,
    }[key];
    closeAllMoveDropdowns();
    if (currentlyOpen) return;
    if (key === 'same') setShowMoveTypeDropdown(true);
    if (key === 'course') setShowMoveDestCourseDropdown(true);
    if (key === 'courseType') setShowMoveDestCourseTypeDropdown(true);
    if (key === 'grade') setShowMoveDestGradeDropdown(true);
    if (key === 'gradeType') setShowMoveDestGradeTypeDropdown(true);
    if (key === 'nonCourse') setShowMoveDestNonCourseDropdown(true);
  };

  const fetchMoveDestCourseTypes = async (courseId) => {
    const gen = ++moveDestCourseFetchGenRef.current;
    if (courseId == null) {
      setMoveDestCourseTypes([]);
      setLoadingMoveDestCourseTypes(false);
      return;
    }
    setLoadingMoveDestCourseTypes(true);
    try {
      const response = await fetch(exerciseTypesByCourseUrl(courseId));
      const data = response.ok ? await response.json() : [];
      if (gen !== moveDestCourseFetchGenRef.current) return;
      setMoveDestCourseTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching move destination course types:', error);
      if (gen !== moveDestCourseFetchGenRef.current) return;
      setMoveDestCourseTypes([]);
    } finally {
      if (gen === moveDestCourseFetchGenRef.current) {
        setLoadingMoveDestCourseTypes(false);
      }
    }
  };

  const fetchMoveDestGradeTypes = async (gradeId) => {
    const gen = ++moveDestGradeFetchGenRef.current;
    if (gradeId == null) {
      setMoveDestGradeTypes([]);
      setLoadingMoveDestGradeTypes(false);
      return;
    }
    setLoadingMoveDestGradeTypes(true);
    try {
      const response = await fetch(exerciseTypesByGradeUrl(gradeId));
      const data = response.ok ? await response.json() : [];
      if (gen !== moveDestGradeFetchGenRef.current) return;
      setMoveDestGradeTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching move destination grade types:', error);
      if (gen !== moveDestGradeFetchGenRef.current) return;
      setMoveDestGradeTypes([]);
    } finally {
      if (gen === moveDestGradeFetchGenRef.current) {
        setLoadingMoveDestGradeTypes(false);
      }
    }
  };

  const clearMoveDestCourse = () => {
    moveDestCourseFetchGenRef.current += 1;
    setMoveDestCourseId(null);
    setMoveDestCourseTypes([]);
    setLoadingMoveDestCourseTypes(false);
    setShowMoveDestCourseDropdown(false);
    setShowMoveDestCourseTypeDropdown(false);
  };

  const clearMoveDestGrade = () => {
    moveDestGradeFetchGenRef.current += 1;
    setMoveDestGradeId(null);
    setMoveDestGradeTypes([]);
    setLoadingMoveDestGradeTypes(false);
    setShowMoveDestGradeDropdown(false);
    setShowMoveDestGradeTypeDropdown(false);
  };

  const handleMoveTargetTypeSelect = (exerciseType) => {
    clearMoveDestCourse();
    clearMoveDestGrade();
    setShowMoveDestNonCourseDropdown(false);
    setMoveDestKind('same');
    setMoveTargetTypeId(exerciseType.id);
    setShowMoveTypeDropdown(false);
  };

  const handleMoveDestCourseSelect = (course) => {
    if (editSelectionSource === 'course' && editCourseId === course.id) return;
    clearMoveDestGrade();
    setShowMoveTypeDropdown(false);
    setShowMoveDestNonCourseDropdown(false);
    setMoveDestKind('course');
    setMoveTargetTypeId(null);
    setMoveDestCourseId(course.id);
    setMoveDestCourseTypes([]);
    setShowMoveDestCourseDropdown(false);
    setShowMoveDestCourseTypeDropdown(true);
    void fetchMoveDestCourseTypes(course.id);
  };

  const handleMoveDestCourseTypeSelect = (exerciseType) => {
    if (exerciseType.id === editExerciseTypeId) return;
    setMoveDestKind('course');
    setMoveTargetTypeId(exerciseType.id);
    setShowMoveDestCourseTypeDropdown(false);
  };

  const handleMoveDestGradeSelect = (grade) => {
    if (editSelectionSource === 'grade' && editGradeId === grade.id) return;
    clearMoveDestCourse();
    setShowMoveTypeDropdown(false);
    setShowMoveDestNonCourseDropdown(false);
    setMoveDestKind('grade');
    setMoveTargetTypeId(null);
    setMoveDestGradeId(grade.id);
    setMoveDestGradeTypes([]);
    setShowMoveDestGradeDropdown(false);
    setShowMoveDestGradeTypeDropdown(true);
    void fetchMoveDestGradeTypes(grade.id);
  };

  const handleMoveDestGradeTypeSelect = (exerciseType) => {
    if (exerciseType.id === editExerciseTypeId) return;
    setMoveDestKind('grade');
    setMoveTargetTypeId(exerciseType.id);
    setShowMoveDestGradeTypeDropdown(false);
  };

  const handleMoveDestNonCourseTypeSelect = (exerciseType) => {
    if (exerciseType.id === editExerciseTypeId) return;
    clearMoveDestCourse();
    clearMoveDestGrade();
    setShowMoveTypeDropdown(false);
    setMoveDestKind('nonCourse');
    setMoveTargetTypeId(exerciseType.id);
    setShowMoveDestNonCourseDropdown(false);
  };

  const handleMoveExercisesCancel = () => {
    resetMoveExerciseState();
  };

  const handleMoveExercisesUpdate = async () => {
    if (
      moveSelectedExerciseIds.size === 0 ||
      moveTargetTypeId == null ||
      moveTargetTypeId === editExerciseTypeId ||
      editExerciseTypeId == null
    ) {
      return;
    }
    setMovingExercises(true);
    try {
      const ids = [...moveSelectedExerciseIds];
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`${getApiUrl()}/api/exercises/${encodeURIComponent(String(id))}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ exercise_type_id: moveTargetTypeId }),
          })
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        throw new Error(`Failed to move ${failed.length} exercise(s). Please try again.`);
      }
      showEditUpdateToast(
        'success',
        ids.length === 1
          ? 'Exercise moved successfully.'
          : `${ids.length} exercises moved successfully.`
      );
      resetMoveExerciseState();
      await fetchEditExercisesForType(editExerciseTypeId);
    } catch (error) {
      console.error('Error moving exercises:', error);
      showEditUpdateToast('error', error.message || 'Failed to move exercise(s).');
    } finally {
      setMovingExercises(false);
    }
  };

  const handleEditCancel = () => {
    resetMoveExerciseState();
    setEditCourseId(null);
    setEditExerciseTypes([]);
    setEditGradeId(null);
    setEditGradeExerciseTypes([]);
    setEditNonCourseExerciseTypes([]);
    setEditSelectionSource(null);
    setEditExerciseTypeId(null);
    setEditExercises([]);
    setEditingExerciseId(null);
    setLoadingEditExercises(false);
    setEditUpdateToast(null);
    setShowEditCourseDropdown(false);
    setShowEditCourseTypesDropdown(false);
    setShowEditGradeDropdown(false);
    setShowEditGradeTypesDropdown(false);
    setShowEditNonCourseTypesDropdown(false);
    resetFormFieldsOnly();
    setSelectedExerciseTypeId(null);
    setShowAssignStudentsDropdown(false);
    setSelectedAssignStudentIds(buildDefaultAssignStudentIds(assignStudents));
    setOpenExerciseDeleteMenuId(null);
    if (!deletingExercise) {
      setExerciseDeleteTarget(null);
      setExerciseDeleteTypedValue('');
      setExerciseDeleteError('');
    }
    void fetchEditNonCourseExerciseTypes();
  };

  const populateFormFromExercise = (exercise) => {
    setExerciseName(exercise.exercise_name || '');
    setSets(String(exercise.sets ?? 1));
    setReps(String(exercise.reps ?? 1));
    setIsTimeBasedExercise(
      exercise.is_time_based_exercise === true || exercise.is_time_based_exercise === 'true'
    );
    setIsHeightBased(
      exercise.is_height_based === true || exercise.is_height_based === 'true'
    );
    setIsWeightBased(
      exercise.is_weight_based === true ||
        exercise.is_weight_based === 'true' ||
        exercise.isWeightBased === true
    );
    setExerciseTime(
      exercise.time != null && exercise.time !== '' ? String(exercise.time) : '30'
    );
    setDescription(exercise.description || '');
    setSelectedExerciseTypeId(exercise.exercise_type_id ?? null);
    const csvImages = parseDisplayImagesCsv(exercise.display_images);
    setPendingImages([]);
    const url = exercise.display_url ? String(exercise.display_url).trim() : '';
    const hasDisplayUrl = url && url.toLowerCase() !== 'null';
    let videoUrl = null;
    let allImages = [...csvImages];
    let legacyDisplayUrlImages = [];

    if (hasDisplayUrl) {
      if (isLikelyImageUrl(url)) {
        legacyDisplayUrlImages = [url];
        const normalized = normalizeMediaUrl(url);
        if (!allImages.some((u) => normalizeMediaUrl(u) === normalized)) {
          allImages = [...allImages, url];
        }
        setVideoSourceMode('upload');
        setDisplayUrl('');
        clearPendingVideo();
      } else {
        videoUrl = url;
        setVideoSourceMode('url');
        setDisplayUrl(url);
        clearPendingVideo();
      }
    } else {
      setVideoSourceMode('upload');
      setDisplayUrl('');
      clearPendingVideo();
    }

    setExistingDisplayImages(allImages);
    setDisplayUrlOnlyImages(legacyDisplayUrlImages);
    setExistingDisplayVideoUrl(videoUrl);
    const orderNum = exercise.exercise_order ?? 0;
    setIsSequenceExercise(orderNum > 0);
    if (orderNum > 0 && exercise.exercise_type_id) {
      void loadExerciseOrderList(exercise.exercise_type_id, exercise.exercise_name, {
        forEdit: true,
      });
    } else {
      setExerciseOrderList([]);
    }
    setMyExerciseOnly(readExerciseMyOnlyFlag(exercise));
  };

  const handleEditCourseSelect = (course) => {
    if (editCourseId === course.id) {
      handleEditCancel();
      return;
    }
    setShowEditNonCourseTypesDropdown(false);
    clearEditGradeSelection();
    setEditCourseId(course.id);
    setEditExerciseTypeId(null);
    setEditExercises([]);
    setEditingExerciseId(null);
    setEditUpdateToast(null);
    setEditSelectionSource(null);
    resetMoveExerciseState();
    resetFormFieldsOnly();
    setShowEditCourseDropdown(false);
    setShowEditCourseTypesDropdown(false);
    fetchEditExerciseTypesForCourse(course.id);
  };

  const handleEditGradeSelect = (grade) => {
    if (editGradeId === grade.id && editSelectionSource === 'grade') {
      clearEditGradeSelection();
      setEditExerciseTypeId(null);
      setEditExercises([]);
      setEditingExerciseId(null);
      setEditSelectionSource(null);
      resetMoveExerciseState();
      resetFormFieldsOnly();
      return;
    }
    setShowEditNonCourseTypesDropdown(false);
    setEditCourseId(null);
    setEditExerciseTypes([]);
    setEditGradeId(grade.id);
    setEditExerciseTypeId(null);
    setEditExercises([]);
    setEditingExerciseId(null);
    setEditUpdateToast(null);
    setEditSelectionSource(null);
    resetMoveExerciseState();
    resetFormFieldsOnly();
    setShowEditGradeDropdown(false);
    setShowEditGradeTypesDropdown(false);
    void fetchEditGradeExerciseTypes(grade.id);
  };

  const handleEditExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'course' && editExerciseTypeId === exerciseType.id) {
      setEditExerciseTypeId(null);
      setEditExercises([]);
      setEditingExerciseId(null);
      resetMoveExerciseState();
      resetFormFieldsOnly();
      return;
    }
    setEditSelectionSource('course');
    setEditExerciseTypeId(exerciseType.id);
    setEditingExerciseId(null);
    setEditUpdateToast(null);
    resetMoveExerciseState();
    resetFormFieldsOnly();
    setShowEditCourseTypesDropdown(false);
    void fetchEditExercisesForType(exerciseType.id);
  };

  const handleEditGradeExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'grade' && editExerciseTypeId === exerciseType.id) {
      setEditExerciseTypeId(null);
      setEditExercises([]);
      setEditingExerciseId(null);
      resetMoveExerciseState();
      resetFormFieldsOnly();
      return;
    }
    setEditSelectionSource('grade');
    setEditExerciseTypeId(exerciseType.id);
    setEditingExerciseId(null);
    setEditUpdateToast(null);
    resetMoveExerciseState();
    resetFormFieldsOnly();
    setShowEditGradeTypesDropdown(false);
    void fetchEditExercisesForType(exerciseType.id);
  };

  const handleEditNonCourseExerciseTypeSelect = (exerciseType) => {
    if (editSelectionSource === 'nonCourse' && editExerciseTypeId === exerciseType.id) {
      setEditExerciseTypeId(null);
      setEditExercises([]);
      setEditingExerciseId(null);
      resetMoveExerciseState();
      resetFormFieldsOnly();
      return;
    }
    setEditCourseId(null);
    setEditExerciseTypes([]);
    clearEditGradeSelection();
    setEditSelectionSource('nonCourse');
    setEditExerciseTypeId(exerciseType.id);
    setEditingExerciseId(null);
    setEditUpdateToast(null);
    resetMoveExerciseState();
    resetFormFieldsOnly();
    setShowEditNonCourseTypesDropdown(false);
    void fetchEditExercisesForType(exerciseType.id);
  };

  const handleEditExerciseSelect = (exercise) => {
    setOpenExerciseDeleteMenuId(null);
    setEditingExerciseId(exercise.id);
    setEditUpdateToast(null);
    populateFormFromExercise(exercise);
    void loadAssignSelectionsForExercise(exercise.id, {
      isMyExerciseOnly: readExerciseMyOnlyFlag(exercise),
    });
  };

  const adoptionExerciseDeepLinkRef = useRef(false);

  useEffect(() => {
    const exerciseId = route?.params?.openEditExerciseId;
    const ctx = route?.params?.adoptionEditContext;
    if (!exerciseId || adoptionExerciseDeepLinkRef.current || !currentUser?.id) return;

    adoptionExerciseDeepLinkRef.current = true;
    void (async () => {
      try {
        setBuilderTab('edit');
        setEditUpdateToast(null);
        resetMoveExerciseState();
        const source = ctx?.selection_source || 'nonCourse';
        setEditSelectionSource(source);

        if (source === 'course' && ctx?.course_id != null) {
          setEditGradeId(null);
          setEditGradeExerciseTypes([]);
          setEditCourseId(Number(ctx.course_id));
          await fetchEditExerciseTypesForCourse(Number(ctx.course_id));
        } else if (source === 'grade' && ctx?.grade_id != null) {
          setEditCourseId(null);
          setEditExerciseTypes([]);
          setEditGradeId(Number(ctx.grade_id));
          await fetchEditGradeExerciseTypes(Number(ctx.grade_id));
        } else {
          setEditCourseId(null);
          setEditGradeId(null);
          setEditExerciseTypes([]);
          setEditGradeExerciseTypes([]);
          await fetchEditNonCourseExerciseTypes();
        }

        if (ctx?.exercise_type_id != null) {
          setEditExerciseTypeId(Number(ctx.exercise_type_id));
          await fetchEditExercisesForType(Number(ctx.exercise_type_id));
        }

        const res = await fetch(exerciseByIdUrl(exerciseId, { viewerId: currentUser.id }));
        if (!res.ok) throw new Error('Exercise not found');
        const exercise = await res.json();
        setEditingExerciseId(exercise.id);
        populateFormFromExercise(exercise);
        void loadAssignSelectionsForExercise(exercise.id, {
          isMyExerciseOnly: readExerciseMyOnlyFlag(exercise),
        });
      } catch (err) {
        console.error('Adoption deep link to exercise:', err);
        adoptionExerciseDeepLinkRef.current = false;
      }
    })();
  }, [route?.params?.openEditExerciseId, route?.params?.adoptionEditContext, currentUser?.id]);

  const reloadEditingExercise = async () => {
    if (editingExerciseId == null) return;
    resetMoveExerciseState();
    setEditUpdateToast(null);
    setErrorMessage('');
    try {
      const response = await fetch(
        exerciseByIdUrl(editingExerciseId, { viewerId: currentUser?.id })
      );
      if (!response.ok) {
        throw new Error('Failed to reload exercise');
      }
      const exercise = await response.json();
      populateFormFromExercise(exercise);
      void loadAssignSelectionsForExercise(editingExerciseId, {
        isMyExerciseOnly: readExerciseMyOnlyFlag(exercise),
      });
      if (editExerciseTypeId != null) {
        void fetchEditExercisesForType(editExerciseTypeId);
      }
    } catch (error) {
      console.error('Error reloading exercise:', error);
      showEditUpdateToast('error', 'Could not reload exercise. Please try again.');
    }
  };

  const selectedCourseLabel =
    selectedCourseId != null
      ? courses.find((c) => c.id === selectedCourseId)?.course_name
      : null;
  const selectedGradeLabel =
    selectedGradeId != null ? grades.find((g) => g.id === selectedGradeId)?.grade_name : null;
  const selectedCourseExerciseTypeLabel =
    typeSelectionSource === 'course' && selectedExerciseTypeId != null
      ? exerciseTypes.find((t) => t.id === selectedExerciseTypeId)?.exercise_type_name
      : null;
  const selectedGradeExerciseTypeLabel =
    typeSelectionSource === 'grade' && selectedExerciseTypeId != null
      ? gradeExerciseTypes.find((t) => t.id === selectedExerciseTypeId)?.exercise_type_name
      : null;
  const selectedNonCourseExerciseTypeLabel =
    typeSelectionSource === 'nonCourse' && selectedExerciseTypeId != null
      ? nonCourseExerciseTypes.find((t) => t.id === selectedExerciseTypeId)?.exercise_type_name
      : null;

  const selectedEditCourseLabel =
    editCourseId != null ? courses.find((c) => c.id === editCourseId)?.course_name : null;
  const selectedEditGradeLabel =
    editGradeId != null ? grades.find((g) => g.id === editGradeId)?.grade_name : null;
  const selectedEditCourseExerciseTypeLabel =
    editSelectionSource === 'course' && editExerciseTypeId != null
      ? editExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name
      : null;
  const selectedEditGradeExerciseTypeLabel =
    editSelectionSource === 'grade' && editExerciseTypeId != null
      ? editGradeExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name
      : null;
  const selectedEditNonCourseExerciseTypeLabel =
    editSelectionSource === 'nonCourse' && editExerciseTypeId != null
      ? editNonCourseExerciseTypes.find((t) => t.id === editExerciseTypeId)?.exercise_type_name
      : null;

  const moveTargetExerciseTypes =
    editSelectionSource === 'nonCourse'
      ? editNonCourseExerciseTypes.filter((t) => t.id !== editExerciseTypeId)
      : editSelectionSource === 'grade'
        ? editGradeExerciseTypes.filter((t) => t.id !== editExerciseTypeId)
        : editExerciseTypes.filter((t) => t.id !== editExerciseTypeId);

  const currentMoveCourseId = editSelectionSource === 'course' ? editCourseId : null;
  const currentMoveGradeId = editSelectionSource === 'grade' ? editGradeId : null;
  const currentMoveExerciseTypeId =
    editSelectionSource === 'nonCourse' ? editExerciseTypeId : null;

  const selectedSameContextMoveTypeName =
    moveDestKind === 'same' && moveTargetTypeId != null
      ? moveTargetExerciseTypes.find((t) => t.id === moveTargetTypeId)?.exercise_type_name
      : null;
  const selectedMoveDestCourseName =
    moveDestKind === 'course' && moveDestCourseId != null
      ? courses.find((c) => c.id === moveDestCourseId)?.course_name
      : null;
  const selectedMoveDestCourseTypeName =
    moveDestKind === 'course' && moveTargetTypeId != null
      ? moveDestCourseTypes.find((t) => t.id === moveTargetTypeId)?.exercise_type_name
      : null;
  const selectedMoveDestGradeName =
    moveDestKind === 'grade' && moveDestGradeId != null
      ? grades.find((g) => g.id === moveDestGradeId)?.grade_name
      : null;
  const selectedMoveDestGradeTypeName =
    moveDestKind === 'grade' && moveTargetTypeId != null
      ? moveDestGradeTypes.find((t) => t.id === moveTargetTypeId)?.exercise_type_name
      : null;
  const selectedMoveDestNonCourseTypeName =
    moveDestKind === 'nonCourse' && moveTargetTypeId != null
      ? editNonCourseExerciseTypes.find((t) => t.id === moveTargetTypeId)?.exercise_type_name
      : null;

  const canSubmitMoveExercises =
    moveSelectedExerciseIds.size > 0 &&
    moveTargetTypeId != null &&
    moveTargetTypeId !== editExerciseTypeId &&
    !movingExercises;

  const selectedEditExerciseTypeName =
    selectedEditCourseExerciseTypeLabel ||
    selectedEditGradeExerciseTypeLabel ||
    selectedEditNonCourseExerciseTypeLabel ||
    'type';

  const activeNewPath =
    typeSelectionSource ??
    (selectedCourseId != null ? 'course' : selectedGradeId != null ? 'grade' : null);

  const creatableCourses = useMemo(
    () => filterCoursesForExerciseCreation(currentUser, courses),
    [currentUser, courses]
  );

  const canCreateNonCourse = canCreateNonCourseExerciseOrType(currentUser);

  const isNewCourseSectionLocked = activeNewPath != null && activeNewPath !== 'course';
  const isNewGradeSectionLocked = activeNewPath != null && activeNewPath !== 'grade';
  const isNewNonCourseSectionLocked =
    !canCreateNonCourse || (activeNewPath != null && activeNewPath !== 'nonCourse');

  const activeEditPath =
    editSelectionSource ??
    (editCourseId != null ? 'course' : editGradeId != null ? 'grade' : null);

  const isEditCourseSectionLocked = activeEditPath != null && activeEditPath !== 'course';
  const isEditGradeSectionLocked = activeEditPath != null && activeEditPath !== 'grade';
  const isEditNonCourseSectionLocked = activeEditPath != null && activeEditPath !== 'nonCourse';

  const handlePageRefresh = () => {
    if (saving) return;
    if (editingExerciseId != null) {
      void reloadEditingExercise();
      return;
    }
    if (builderTab === 'edit') {
      handleEditCancel();
    } else {
      handleReset();
    }
    void fetchCourses();
    void fetchGrades();
    void fetchNonCourseExerciseTypes();
    void fetchEditNonCourseExerciseTypes();
  };

  useEffect(() => {
    if (isNewCourseSectionLocked) {
      setShowCourseDropdown(false);
      setShowCourseTypesDropdown(false);
    }
  }, [isNewCourseSectionLocked]);

  useEffect(() => {
    if (isNewGradeSectionLocked) {
      setShowGradeDropdown(false);
      setShowGradeTypesDropdown(false);
    }
  }, [isNewGradeSectionLocked]);

  useEffect(() => {
    if (isNewNonCourseSectionLocked) {
      setShowNonCourseTypesDropdown(false);
    }
  }, [isNewNonCourseSectionLocked]);

  useEffect(() => {
    if (isEditCourseSectionLocked) {
      setShowEditCourseDropdown(false);
      setShowEditCourseTypesDropdown(false);
    }
  }, [isEditCourseSectionLocked]);

  useEffect(() => {
    if (isEditGradeSectionLocked) {
      setShowEditGradeDropdown(false);
      setShowEditGradeTypesDropdown(false);
    }
  }, [isEditGradeSectionLocked]);

  useEffect(() => {
    if (isEditNonCourseSectionLocked) {
      setShowEditNonCourseTypesDropdown(false);
    }
  }, [isEditNonCourseSectionLocked]);

  const switchBuilderTab = (tab) => {
    setBuilderTab(tab);
    setSaveStatus(null);
    setErrorMessage('');
    setEditUpdateToast(null);
    if (tab === 'edit') {
      setSelectedCourseId(null);
      setSelectedExerciseTypeId(null);
    } else {
      handleEditCancel();
    }
  };

  const formatExerciseYoutubeTitle = (name) => {
    const n = String(name || '').trim();
    return n ? `Exercise: ${n}` : 'Exercise';
  };

  const clearPendingVideo = () => {
    revokeMobileWebVideoBlobUri(webVideoBlobUriRef.current);
    webVideoBlobUriRef.current = null;
    if (pendingVideo?.uri && String(pendingVideo.uri).startsWith('blob:') && typeof URL !== 'undefined') {
      try {
        URL.revokeObjectURL(pendingVideo.uri);
      } catch {
        /* ignore */
      }
    }
    setPendingVideo(null);
    webVideoFileRef.current = null;
    setVideoPickError('');
    setShowFilesFallback(false);
    if (webVideoFileInputRef.current) {
      webVideoFileInputRef.current.value = '';
    }
  };

  const applyWebVideoSelection = (file, pickSource = 'file-input') => {
    if (!file) return;
    setVideoPickError('');
    setShowFilesFallback(false);
    webVideoFileRef.current = file;
    revokeMobileWebVideoBlobUri(webVideoBlobUriRef.current);
    const blobUri = createMobileWebVideoBlobUri(file);
    webVideoBlobUriRef.current = blobUri;
    const pickedName = file.name || 'video.mp4';
    setPendingVideo({
      fileName: pickedName,
      mimeType: file.type || 'video/mp4',
      fileSize: file.size > 0 ? file.size : undefined,
      uri: blobUri || undefined,
      pickSource,
    });

    if (!isMobileWeb()) return;

    // Gallery picks on Android often report size 0 — try an immediate in-memory read.
    if (pickSource === 'file-input' && !(file.size > 0)) {
      startMobileWebVideoMaterialize(file, {
        onReady: (mat) => {
          webVideoFileRef.current = mat;
          revokeMobileWebVideoBlobUri(webVideoBlobUriRef.current);
          const matBlob = createMobileWebVideoBlobUri(mat);
          webVideoBlobUriRef.current = matBlob;
          setVideoPickError('');
          setShowFilesFallback(false);
          setPendingVideo({
            fileName: mat.name,
            mimeType: mat.type,
            fileSize: mat.size,
            uri: matBlob || undefined,
            pickSource: 'file-input-materialized',
          });
        },
        onError: () => {
          setVideoPickError(
            'Couldn\u2019t read this video from Gallery. Tap Browse Files and pick the same video from Downloads.'
          );
          setShowFilesFallback(true);
        },
      });
      return;
    }

    validateMobileWebVideoSelection({ file, blobUri, pickSource }).then((result) => {
      if (result.ok) {
        setVideoPickError('');
        setShowFilesFallback(false);
        if (result.fileSize > 0) {
          setPendingVideo((prev) => (prev ? { ...prev, fileSize: result.fileSize } : prev));
        }
      } else {
        setVideoPickError(result.message);
        setShowFilesFallback(Boolean(result.needsFilesFallback));
      }
    });
  };

  const onMobileWebPickVideo = () => {
    if (Platform.OS !== 'web' || !isMobileWeb()) return;
    setVideoPickError('');
    setShowFilesFallback(false);
    webVideoFileInputRef.current?.click?.();
  };

  const onMobileWebBrowseFiles = async () => {
    if (Platform.OS !== 'web' || !isMobileWeb()) return;
    setVideoPickError('');
    setShowFilesFallback(false);
    try {
      const file = await pickVideoViaShowOpenFilePicker();
      if (file) applyWebVideoSelection(file, 'showOpenFilePicker');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setVideoPickError('Could not open the file browser. Please try again.');
    }
  };

  const pickVideoFromDevice = async () => {
    if (Platform.OS === 'web') {
      return;
    }
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission needed', 'Allow photo library access to choose a video.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync(getExerciseVideoPickerOptions());
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      const fileName =
        a.fileName || (a.uri && String(a.uri).split(/[/\\]/).pop()) || 'video.mp4';
      if (!a.uri) {
        Alert.alert('Video', 'Could not read the selected video. Please try again.');
        return;
      }
      setPendingVideo({
        fileName,
        uri: a.uri,
        fileSize: a.fileSize,
        mimeType: a.mimeType,
      });
    } catch (error) {
      console.error(error);
      Alert.alert('Video', error.message || 'Could not pick video.');
    }
  };

  const onWebVideoFileChange = (e) => {
    const input = e.nativeEvent?.target || e.target;
    const file = input?.files?.[0];
    if (!file) return;
    applyWebVideoSelection(file, 'file-input');
  };

  const uploadExerciseVideoToYoutube = async (exerciseNameForTitle) => {
    if (!pendingVideo) {
      throw new Error('Please choose a video file first.');
    }
    const input = webVideoFileInputRef.current;
    const materializedFile =
      Platform.OS === 'web' && webVideoFileRef.current?._materializedForUpload
        ? webVideoFileRef.current
        : undefined;
    const webBlobUri =
      Platform.OS === 'web' && !materializedFile
        ? webVideoBlobUriRef.current ||
          (pendingVideo?.uri && String(pendingVideo.uri).startsWith('blob:') ? pendingVideo.uri : undefined)
        : undefined;
    const webFile =
      Platform.OS === 'web'
        ? materializedFile ||
          (!webBlobUri ? input?.files?.[0] || webVideoFileRef.current : undefined)
        : undefined;
    if (Platform.OS === 'web' && webFile) {
      webVideoFileRef.current = webFile;
    }
    const title = formatExerciseYoutubeTitle(exerciseNameForTitle);
    return uploadYoutubeVideoFile({
      uri: Platform.OS !== 'web' ? pendingVideo.uri : undefined,
      webFile,
      webBlobUri,
      fileName: pendingVideo.fileName,
      mimeType: pendingVideo.mimeType,
      fileSize: pendingVideo.fileSize,
      title,
      description: title,
      privacyStatus: 'unlisted',
      isShort: false,
      account_key: 'default',
    });
  };

  const handlePickImages = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission needed', 'Allow photo library access to attach images.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.85,
      });
      if (result.canceled) return;
      const assets = result.assets || [];
      const batch = assets.map((a, i) => ({
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        uri: a.uri,
        fileName: a.fileName || a.uri.split(/[/\\]/).pop() || `image_${i}.jpg`,
      }));
      setPendingImages((prev) => [...prev, ...batch]);
    } catch (error) {
      console.error(error);
      Alert.alert('Image picker', error.message || 'Could not open photo library.');
    }
  };

  const removePendingImage = (id) => {
    setPendingImages((prev) => prev.filter((p) => p.id !== id));
  };

  const displayImageFileName = (url) => {
    try {
      return decodeURIComponent(String(url).split('/').pop() || url);
    } catch {
      return String(url).split('/').pop() || url;
    }
  };

  const openRemoveMediaConfirm = (pending) => {
    removeMediaPendingRef.current = pending;
    setRemoveMediaConfirm(pending);
  };

  const dismissRemoveMediaConfirm = () => {
    removeMediaPendingRef.current = null;
    setRemoveMediaConfirm(null);
  };

  const confirmRemoveExistingDisplayImage = (url) => {
    const viaDisplayUrl = displayUrlOnlyImages.some(
      (u) => normalizeMediaUrl(u) === normalizeMediaUrl(url)
    );
    openRemoveMediaConfirm({ kind: 'image', url, viaDisplayUrl });
  };

  const removeExistingDisplayImage = async (url) => {
    if (editingExerciseId == null || saving) return;
    setRemovingDisplayImageUrl(url);
    try {
      const response = await fetch(exerciseRemoveDisplayImageUrl(editingExerciseId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_url: url }),
      });
      const raw = await response.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        /* ignore */
      }
      if (!response.ok) {
        throw new Error(body.error || 'Failed to delete image');
      }
      const updatedUrls = parseDisplayImagesCsv(body.exercise?.display_images);
      setExistingDisplayImages(updatedUrls);
      setEditExercises((prev) =>
        prev.map((ex) =>
          ex.id === editingExerciseId
            ? { ...ex, display_images: body.exercise?.display_images ?? null }
            : ex
        )
      );
    } catch (error) {
      console.error('Error removing exercise image:', error);
      Alert.alert('Error', error.message || 'Could not delete image.');
    } finally {
      setRemovingDisplayImageUrl(null);
    }
  };

  const confirmRemoveExistingDisplayVideo = () => {
    const url = String(existingDisplayVideoUrl ?? '').trim();
    if (!url) return;
    const kind = isLikelyImageUrl(url) ? 'image' : 'video';
    openRemoveMediaConfirm({ kind, url, viaDisplayUrl: true });
  };

  const handleRemoveMediaConfirm = () => {
    const pending = removeMediaPendingRef.current;
    removeMediaPendingRef.current = null;
    setRemoveMediaConfirm(null);
    if (!pending) return;
    if (pending.viaDisplayUrl) {
      if (!pending.url) return;
      void removeExistingDisplayVideo(pending.url, pending.kind === 'image');
    } else if (pending.url) {
      void removeExistingDisplayImage(pending.url);
    }
  };

  const removeExistingDisplayVideo = async (urlOverride, showImageRowSpinner = false) => {
    const urlToRemove = String(urlOverride ?? existingDisplayVideoUrl ?? '').trim();
    if (editingExerciseId == null || !urlToRemove || saving) return;
    if (showImageRowSpinner) {
      setRemovingDisplayImageUrl(urlToRemove);
    } else {
      setRemovingDisplayVideo(true);
    }
    try {
      const response = await fetch(exerciseRemoveDisplayVideoUrl(editingExerciseId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_url: urlToRemove }),
      });
      const raw = await response.text();
      let body = {};
      try {
        body = raw ? JSON.parse(raw) : {};
      } catch {
        /* ignore */
      }
      if (!response.ok) {
        throw new Error(body.error || 'Failed to delete video');
      }
      const removedNorm = normalizeMediaUrl(urlToRemove);
      setExistingDisplayVideoUrl(null);
      setDisplayUrl('');
      setVideoSourceMode('upload');
      clearPendingVideo();
      setDisplayUrlOnlyImages((prev) => prev.filter((u) => normalizeMediaUrl(u) !== removedNorm));
      setExistingDisplayImages((prev) => prev.filter((u) => normalizeMediaUrl(u) !== removedNorm));
      setEditExercises((prev) =>
        prev.map((ex) =>
          ex.id === editingExerciseId ? { ...ex, display_url: null } : ex
        )
      );
      if (body.warning) {
        Alert.alert('Video removed', body.warning);
      }
    } catch (error) {
      console.error('Error removing exercise video:', error);
      Alert.alert('Error', error.message || 'Could not delete video.');
    } finally {
      if (showImageRowSpinner) {
        setRemovingDisplayImageUrl(null);
      } else {
        setRemovingDisplayVideo(false);
      }
    }
  };

  const handleSave = async () => {
    if (saving) return;

    // Validation
    if (!exerciseName.trim()) {
      setSaveStatus('error');
      setErrorMessage('Exercise Name is required');
      return;
    }

    if (!selectedExerciseTypeId) {
      setSaveStatus('error');
      setErrorMessage('Please select an Exercise Type');
      return;
    }

    const creationAccessError = validateExerciseCreationAccess(currentUser, {
      typeSelectionSource,
      selectedCourseId,
      selectedGradeId,
      courses,
      grades,
    });
    if (creationAccessError) {
      setSaveStatus('error');
      setErrorMessage(creationAccessError);
      return;
    }

    const setsNum = parseInt(sets, 10);
    const repsNum = parseInt(reps, 10);

    if (isNaN(setsNum) || setsNum < 1) {
      setSaveStatus('error');
      setErrorMessage('Sets must be a positive number');
      return;
    }

    if (isNaN(repsNum) || repsNum < 1) {
      setSaveStatus('error');
      setErrorMessage('Reps must be a positive number');
      return;
    }

    const timeNum = parseInt(exerciseTime, 10);
    if (isTimeBasedExercise && (Number.isNaN(timeNum) || timeNum < 0)) {
      setSaveStatus('error');
      setErrorMessage('Time must be a non-negative number of seconds');
      return;
    }

    if (videoSourceMode === 'upload' && videoPickError) {
      setSaveStatus('error');
      setErrorMessage(videoPickError);
      return;
    }

    const pendingCount = pendingImages.length;
    const wantsUrlVideo = videoSourceMode === 'url' && displayUrl.trim();
    const hasDeviceVideo =
      pendingVideo &&
      (pendingVideo.uri || (Platform.OS === 'web' && webVideoFileRef.current));
    const wantsUploadVideo = videoSourceMode === 'upload' && hasDeviceVideo;

    if (videoSourceMode === 'upload' && pendingVideo && !hasDeviceVideo) {
      setSaveStatus('error');
      setErrorMessage('Could not read the selected video. Please choose the file again.');
      return;
    }

    const executeSave = async (skipImageUpload) => {
      setSaving(true);
      let exerciseId = null;
      let savePhase = 'start';
      const apiFetch = (url, init) =>
        Platform.OS !== 'web' && wantsUploadVideo
          ? fetchWithRetry(url, init, { maxAttempts: 3, delayMs: 1200 })
          : fetch(url, init);
      try {
        let youtubeDisplayUrl = wantsUrlVideo ? displayUrl.trim() : null;
        if (wantsUploadVideo) {
          savePhase = 'youtube-upload';
          logYoutubeUpload('exercise-save-phase', {
            context: 'create',
            phase: savePhase,
            uploadMethodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
          });
          youtubeDisplayUrl = await uploadExerciseVideoToYoutube(exerciseName.trim());
          clearPendingVideo();
        }

        savePhase = 'create-exercise-post';
        const createUrl = `${getApiUrl()}/api/exercises`;
        logYoutubeUpload('exercise-save-phase', {
          context: 'create',
          phase: savePhase,
          url: createUrl,
        });
        const response = await apiFetch(createUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            exercise_name: exerciseName.trim(),
            sets: setsNum,
            reps: repsNum,
            is_time_based_exercise: isTimeBasedExercise,
            time: isTimeBasedExercise ? timeNum : null,
            is_height_based: isHeightBased,
            is_weight_based: isWeightBased,
            exercise_type_id: selectedExerciseTypeId,
            description: description.trim() || null,
            display_url: youtubeDisplayUrl || null,
            created_by: currentUser?.id ?? null,
            is_my_exercise_only: myExerciseOnly,
          }),
        });

        const raw = await response.text();
        let created = {};
        try {
          created = raw ? JSON.parse(raw) : {};
        } catch {
          /* ignore */
        }

        if (!response.ok) {
          throw new Error(created.error || 'Failed to create exercise');
        }

        exerciseId = created.id;

        if (!skipImageUpload && pendingCount > 0) {
          const supabase = getSupabaseClient();
          const bucket = getExerciseImagesBucketName();
          const urls = [];
          for (let i = 0; i < pendingImages.length; i += 1) {
            const img = pendingImages[i];
            const safe = sanitizeExerciseFilename(img.fileName);
            const path = `${exerciseId}_${safe}`;
            const body = await uriToArrayBuffer(img.uri);
            const { error } = await supabase.storage.from(bucket).upload(path, body, {
              contentType: guessMimeFromName(img.fileName),
              upsert: false,
            });
            if (error) {
              throw new Error(error.message || 'Storage upload failed');
            }
            const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
            urls.push(pub.publicUrl);
          }

          const displayImagesCsv = urls.join(',');
          const putRes = await apiFetch(`${getApiUrl()}/api/exercises/${encodeURIComponent(String(exerciseId))}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              exercise_name: null,
              sets: null,
              reps: null,
              exercise_type_id: null,
              description: null,
              display_url: youtubeDisplayUrl || null,
              exercise_order: null,
              display_images: displayImagesCsv,
            }),
          });
          const putRaw = await putRes.text();
          let putBody = {};
          try {
            putBody = putRaw ? JSON.parse(putRaw) : {};
          } catch {
            /* ignore */
          }
          if (!putRes.ok) {
            throw new Error(putBody.error || 'Failed to save image URLs');
          }
        }

        if (isSequenceExercise && exerciseOrderList.length > 0) {
          await persistExerciseOrders(exerciseOrderList, exerciseId);
        }

        await assignExerciseToStudents(exerciseId, [...selectedAssignStudentIds], {
          myExerciseOnlyMode: myExerciseOnly,
        });

        setSaveStatus('success');
        setErrorMessage('');
      } catch (error) {
        logExerciseSaveFailure('create', savePhase, error, { exerciseId });
        if (exerciseId != null) {
          try {
            await fetch(`${getApiUrl()}/api/exercises/${encodeURIComponent(String(exerciseId))}`, {
              method: 'DELETE',
            });
          } catch (delErr) {
            console.warn('Exercise rollback delete failed', delErr);
          }
        }
        const msg = formatExerciseSaveError(error);
        if (isYoutubeReconnectError(msg)) {
          youtubeOAuthRef.current?.markNeedsReconnect?.(msg);
        }
        setSaveStatus('error');
        setErrorMessage(msg);
      } finally {
        setSaving(false);
      }
    };

    if (pendingCount > 0) {
      const supabaseClient = getSupabaseClient();
      const missing = getSupabaseConfigHint();
      if (missing.length || !supabaseClient) {
        Alert.alert(
          'Images unavailable',
          'Supabase is not configured (missing URL or anon key). You can save the exercise without photos, or add EXPO_PUBLIC_SUPABASE_URL / EXPO_PUBLIC_SUPABASE_ANON_KEY and reload.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Save without images',
              onPress: () => {
                void executeSave(true);
              },
            },
          ]
        );
        return;
      }
    }

    await executeSave(false);
  };

  const handleUpdate = async () => {
    if (saving) return;

    if (!editingExerciseId) {
      showEditUpdateToast('error');
      return;
    }
    if (!exerciseName.trim()) {
      showEditUpdateToast('error');
      return;
    }
    if (!editExerciseTypeId) {
      showEditUpdateToast('error');
      return;
    }

    const setsNum = parseInt(sets, 10);
    const repsNum = parseInt(reps, 10);
    if (isNaN(setsNum) || setsNum < 1) {
      showEditUpdateToast('error');
      return;
    }
    if (isNaN(repsNum) || repsNum < 1) {
      showEditUpdateToast('error');
      return;
    }

    const timeNum = parseInt(exerciseTime, 10);
    if (isTimeBasedExercise && (Number.isNaN(timeNum) || timeNum < 0)) {
      showEditUpdateToast('error');
      return;
    }

    if (videoSourceMode === 'upload' && videoPickError) {
      showEditUpdateToast('error', videoPickError);
      return;
    }

    const wantsUrlVideo = videoSourceMode === 'url' && displayUrl.trim();
    const hasDeviceVideo =
      pendingVideo &&
      (pendingVideo.uri || (Platform.OS === 'web' && webVideoFileRef.current));
    const wantsUploadVideo = videoSourceMode === 'upload' && hasDeviceVideo;

    if (videoSourceMode === 'upload' && pendingVideo && !hasDeviceVideo) {
      showEditUpdateToast('error');
      return;
    }

    const runUpdate = async (skipImageUpload) => {
      setSaving(true);
      setEditUpdateToast(null);
      let savePhase = 'start';
      const apiFetch = (url, init) =>
        Platform.OS !== 'web' && wantsUploadVideo
          ? fetchWithRetry(url, init, { maxAttempts: 3, delayMs: 1200 })
          : fetch(url, init);
      try {
        let finalDisplayUrl = null;
        if (wantsUploadVideo) {
          savePhase = 'youtube-upload';
          logYoutubeUpload('exercise-save-phase', {
            context: 'update',
            phase: savePhase,
            uploadMethodVersion: YOUTUBE_UPLOAD_METHOD_VERSION,
          });
          finalDisplayUrl = await uploadExerciseVideoToYoutube(exerciseName.trim());
          clearPendingVideo();
        } else if (wantsUrlVideo) {
          finalDisplayUrl = displayUrl.trim();
        } else if (videoSourceMode === 'url') {
          finalDisplayUrl = '';
        }

        let imageUrls = [...existingDisplayImages];
        if (!skipImageUpload && pendingImages.length > 0) {
          savePhase = 'image-upload';
          const supabase = getSupabaseClient();
          const bucket = getExerciseImagesBucketName();
          for (let i = 0; i < pendingImages.length; i += 1) {
            const img = pendingImages[i];
            const safe = sanitizeExerciseFilename(img.fileName);
            const path = `${editingExerciseId}_${safe}`;
            const body = await uriToArrayBuffer(img.uri);
            const { error } = await supabase.storage.from(bucket).upload(path, body, {
              contentType: guessMimeFromName(img.fileName),
              upsert: false,
            });
            if (error) throw new Error(error.message || 'Storage upload failed');
            const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
            imageUrls.push(pub.publicUrl);
          }
          setPendingImages([]);
        }

        const displayImagesCsv = imageUrls.length > 0 ? imageUrls.join(',') : null;

        savePhase = 'update-exercise-put';
        const updateUrl = `${getApiUrl()}/api/exercises/${encodeURIComponent(String(editingExerciseId))}`;
        logYoutubeUpload('exercise-save-phase', {
          context: 'update',
          phase: savePhase,
          url: updateUrl,
        });
        const putRes = await apiFetch(
          updateUrl,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              exercise_name: exerciseName.trim(),
              sets: setsNum,
              reps: repsNum,
              is_time_based_exercise: isTimeBasedExercise,
              time: isTimeBasedExercise ? timeNum : null,
              is_height_based: isHeightBased,
              is_weight_based: isWeightBased,
              exercise_type_id: editExerciseTypeId,
              description: description.trim() || null,
              display_url: finalDisplayUrl,
              display_images: displayImagesCsv,
              exercise_order: null,
              is_my_exercise_only: myExerciseOnly,
            }),
          }
        );
        const putRaw = await putRes.text();
        let putBody = {};
        try {
          putBody = putRaw ? JSON.parse(putRaw) : {};
        } catch {
          /* ignore */
        }
        if (!putRes.ok) {
          throw new Error(putBody.error || 'Failed to update exercise');
        }

        if (isSequenceExercise && exerciseOrderList.length > 0) {
          await persistExerciseOrders(exerciseOrderList);
        }

        await assignExerciseToStudents(editingExerciseId, [...selectedAssignStudentIds], {
          myExerciseOnlyMode: myExerciseOnly,
        });

        setExistingDisplayImages(imageUrls);
        const savedVideoUrl =
          putBody.display_url != null &&
          String(putBody.display_url).trim() !== '' &&
          String(putBody.display_url).trim().toLowerCase() !== 'null'
            ? String(putBody.display_url).trim()
            : null;
        setExistingDisplayVideoUrl(savedVideoUrl);
        if (savedVideoUrl) {
          setDisplayUrl(savedVideoUrl);
          setVideoSourceMode('url');
        } else if (finalDisplayUrl === '') {
          setDisplayUrl('');
          clearPendingVideo();
        }
        showEditUpdateToast('success');
        void fetchEditExercisesForType(editExerciseTypeId);
      } catch (error) {
        logExerciseSaveFailure('update', savePhase, error, {
          editingExerciseId,
        });
        if (isYoutubeReconnectError(error.message)) {
          youtubeOAuthRef.current?.markNeedsReconnect?.(error.message);
        }
        showEditUpdateToast('error', formatExerciseSaveError(error));
      } finally {
        setSaving(false);
      }
    };

    if (pendingImages.length > 0) {
      const supabaseClient = getSupabaseClient();
      const missing = getSupabaseConfigHint();
      if (missing.length || !supabaseClient) {
        Alert.alert(
          'Images unavailable',
          'Supabase is not configured. You can update without new photos, or configure Supabase and try again.',
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Update without new images',
              onPress: () => {
                void runUpdate(true);
              },
            },
          ]
        );
        return;
      }
    }

    await runUpdate(false);
  };

  const { width: windowWidth } = useWindowDimensions();
  const videoCarouselSlideWidth = Math.max(
    1,
    Math.round(windowWidth * EXERCISE_VIDEO_CAROUSEL_WIDTH_RATIO)
  );
  const videoCarouselSlideHeight = Math.round(
    videoCarouselSlideWidth / EXERCISE_VIDEO_CAROUSEL_ASPECT
  );

  const styles = useMemo(
    () => mergeExerciseBuilderStyles(theme, videoCarouselSlideWidth, videoCarouselSlideHeight),
    [theme, videoCarouselSlideWidth, videoCarouselSlideHeight]
  );

  const openExerciseVideoModal = useCallback((uri, title = 'Video', options = {}) => {
    if (!uri) return;
    setExerciseVideoModal({
      uri,
      title: title || 'Video',
      isImage: !!options.isImage,
    });
  }, []);

  const closeExerciseVideoModal = useCallback(() => setExerciseVideoModal(null), []);

  const exerciseVideoCarouselLinks = useMemo(() => {
    const url = String(existingDisplayVideoUrl ?? '').trim();
    if (builderTab !== 'edit' || !url) return [];
    return [
      {
        url,
        break_name: exerciseName.trim() || 'Exercise video',
      },
    ];
  }, [builderTab, existingDisplayVideoUrl, exerciseName]);

  const renderExerciseFormFields = () => (
    <>
      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Exercise Name *</Text>
        <TextInput
          style={styles.input}
          value={exerciseName}
          onChangeText={setExerciseName}
          placeholder="Enter exercise name"
          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          editable={!saving}
        />
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Sets *</Text>
        <TextInput
          style={styles.input}
          value={sets}
          onChangeText={setSets}
          keyboardType="numeric"
          placeholder="1"
          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          editable={!saving}
        />
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Reps *</Text>
        <TextInput
          style={styles.input}
          value={reps}
          onChangeText={setReps}
          keyboardType="numeric"
          placeholder="1"
          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          editable={!saving}
        />
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Exercise tracking</Text>
        <View
          style={styles.exerciseTrackingOptions}
          {...(Platform.OS === 'web'
            ? { dataSet: { exerciseBuilderTrackingOptions: 'true' } }
            : {})}
        >
          <TouchableOpacity
            style={styles.sequenceCheckboxRow}
            onPress={() => setIsTimeBasedExercise((v) => !v)}
            disabled={saving}
            activeOpacity={0.7}
          >
            <Text style={styles.sequenceCheckboxMark}>{isTimeBasedExercise ? '☑' : '☐'}</Text>
            <Text
              style={styles.sequenceCheckboxLabel}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderTrackingOptionText: 'true' } }
                : {})}
            >
              Time-based exercise (holds, etc.)
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.sequenceCheckboxRow}
            onPress={() => setIsHeightBased((v) => !v)}
            disabled={saving}
            activeOpacity={0.7}
          >
            <Text style={styles.sequenceCheckboxMark}>{isHeightBased ? '☑' : '☐'}</Text>
            <Text
              style={styles.sequenceCheckboxLabel}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderTrackingOptionText: 'true' } }
                : {})}
            >
              Height-based exercise
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.sequenceCheckboxRow}
            onPress={() => setIsWeightBased((v) => !v)}
            disabled={saving}
            activeOpacity={0.7}
          >
            <Text style={styles.sequenceCheckboxMark}>{isWeightBased ? '☑' : '☐'}</Text>
            <Text
              style={styles.sequenceCheckboxLabel}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderTrackingOptionText: 'true' } }
                : {})}
            >
              Weight-based exercise
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {isTimeBasedExercise ? (
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Default time (seconds) *</Text>
          <TextInput
            style={styles.input}
            value={exerciseTime}
            onChangeText={setExerciseTime}
            keyboardType="numeric"
            placeholder="30"
            placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
            editable={!saving}
          />
        </View>
      ) : null}

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Description</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={description}
          onChangeText={setDescription}
          placeholder="Enter exercise description"
          placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          editable={!saving}
        />
      </View>

      <View style={styles.fieldContainer}>
        <TouchableOpacity
          style={styles.sequenceCheckboxRow}
          onPress={() => {
            const next = !isSequenceExercise;
            setIsSequenceExercise(next);
            if (!next) setExerciseOrderList([]);
          }}
          disabled={saving}
          activeOpacity={0.7}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isSequenceExercise }}
        >
          <Text style={styles.sequenceCheckboxMark}>{isSequenceExercise ? '☑' : '☐'}</Text>
          <Text style={styles.sequenceCheckboxLabel}>Is this exercise 1 of a sequence?</Text>
        </TouchableOpacity>
        <Text style={styles.sequenceHintText}>
          if the exercise is part of a sequence of exercises, they belong in their own exercise type
          on its own
        </Text>

        {isSequenceExercise ? (
          <View style={styles.exerciseOrderSection}>
            <Text style={styles.exerciseOrderTitle}>Exercise Order</Text>
            {!selectedExerciseTypeId ? (
              <Text style={styles.helpText}>
                {builderTab === 'edit'
                  ? 'Exercise type is required for ordering.'
                  : 'Select an exercise type above to load and order exercises in this sequence.'}
              </Text>
            ) : loadingExerciseOrder ? (
              <ActivityIndicator
                style={styles.exerciseOrderLoading}
                color={theme.colors.primary || '#d4af37'}
              />
            ) : exerciseOrderList.length === 0 ? (
              <Text style={styles.helpText}>No exercises to order yet.</Text>
            ) : (
              <>
                <Text style={styles.helpText}>
                  Use ↑ ↓ to set the order for this exercise type. Position 1 is shown first in
                  Session Builder and Assignments.
                </Text>
                <ScrollView
                  style={styles.exerciseOrderList}
                  nestedScrollEnabled
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderOrderList: 'true' } }
                    : {})}
                >
                  {exerciseOrderList.map((item, index) => {
                    const isCurrent =
                      item.id === NEW_EXERCISE_ORDER_ID ||
                      (builderTab === 'edit' && item.id === editingExerciseId);
                    return (
                      <View
                        key={String(item.id)}
                        style={styles.exerciseOrderItem}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { exerciseBuilderOrderItem: 'true' } }
                          : {})}
                      >
                        <View style={styles.exerciseOrderControls}>
                          <TouchableOpacity
                            style={[styles.orderButton, index === 0 && styles.orderButtonDisabled]}
                            onPress={() => handleMoveExerciseOrder(index, 'up')}
                            disabled={index === 0 || saving}
                          >
                            <Text style={styles.orderButtonText}>↑</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.orderButton,
                              index === exerciseOrderList.length - 1 && styles.orderButtonDisabled,
                            ]}
                            onPress={() => handleMoveExerciseOrder(index, 'down')}
                            disabled={index === exerciseOrderList.length - 1 || saving}
                          >
                            <Text style={styles.orderButtonText}>↓</Text>
                          </TouchableOpacity>
                        </View>
                        <Text style={styles.exerciseOrderItemName} numberOfLines={2}>
                          {index + 1}. {item.exercise_name}
                          {isCurrent ? ' (this exercise)' : ''}
                        </Text>
                      </View>
                    );
                  })}
                </ScrollView>
              </>
            )}
          </View>
        ) : null}
      </View>

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Images</Text>
        {builderTab === 'edit' && existingDisplayImages.length > 0 ? (
          <View style={styles.existingImagesBlock}>
            <Text style={styles.helpText}>Current images on this exercise:</Text>
            {existingDisplayImages.map((url, idx) => {
              const fileName = displayImageFileName(url);
              const isRemoving = removingDisplayImageUrl === url;
              return (
                <View
                  key={`${url}-${idx}`}
                  style={styles.pendingRow}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderExistingImageRow: 'true' } }
                    : {})}
                >
                  <Text style={styles.pendingName} numberOfLines={1}>
                    {fileName}
                  </Text>
                  <TouchableOpacity
                    onPress={() => confirmRemoveExistingDisplayImage(url)}
                    hitSlop={12}
                    disabled={saving || isRemoving}
                    accessibilityRole="button"
                    accessibilityLabel={`Delete ${fileName}`}
                    style={styles.existingImageRemoveHit}
                    {...(Platform.OS === 'web'
                      ? { dataSet: { exerciseBuilderMediaRemoveBtn: 'true' } }
                      : {})}
                  >
                    {isRemoving ? (
                      <ActivityIndicator size="small" color="#ffffff" />
                    ) : (
                      <MaterialIcons name="close" size={22} color="#ffffff" />
                    )}
                  </TouchableOpacity>
                </View>
              );
            })}
          </View>
        ) : null}
        <TouchableOpacity
          style={styles.uploadButton}
          onPress={handlePickImages}
          activeOpacity={0.8}
          disabled={saving}
        >
          <Text style={styles.uploadButtonText}>
            {builderTab === 'edit' ? 'Add image' : 'Upload image'}
          </Text>
        </TouchableOpacity>
        {pendingImages.map((img) => (
          <View
            key={img.id}
            style={styles.pendingRow}
            {...(Platform.OS === 'web' ? { dataSet: { exerciseBuilderPendingFile: 'true' } } : {})}
          >
            <Text style={styles.pendingName} numberOfLines={1}>
              {img.fileName}
            </Text>
            <TouchableOpacity
              onPress={() => removePendingImage(img.id)}
              hitSlop={12}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel={`Remove ${img.fileName}`}
              style={styles.pendingRemoveHit}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderMediaRemoveBtn: 'true' } }
                : {})}
            >
              <Text style={styles.pendingRemove}>✕</Text>
            </TouchableOpacity>
          </View>
        ))}
      </View>

      {videoSourceMode === 'upload' ? (
        <YoutubeOAuthBar ref={youtubeOAuthRef} containerStyle={styles.youtubeOAuthBar} />
      ) : null}

      <View style={styles.fieldContainer}>
        <Text style={styles.label}>Video</Text>
        {builderTab === 'edit' && exerciseVideoCarouselLinks.length > 0 ? (
          <View
            style={styles.exerciseVideoCarouselBlock}
            {...(Platform.OS === 'web'
              ? { dataSet: { exerciseBuilderVideoCarousel: 'true' } }
              : {})}
          >
            {removingDisplayVideo ? (
              <ActivityIndicator
                style={styles.exerciseVideoCarouselLoading}
                color={theme.colors.primary || '#d4af37'}
              />
            ) : null}
            <ExternalBreaksCarousel
              links={exerciseVideoCarouselLinks}
              theme={theme}
              styles={styles}
              slideWidth={videoCarouselSlideWidth}
              slideHeight={videoCarouselSlideHeight}
              onOpenVideo={openExerciseVideoModal}
              editable={!removingDisplayVideo && !saving}
              onRemoveBreak={
                !removingDisplayVideo && !saving ? confirmRemoveExistingDisplayVideo : undefined
              }
              removeButtonDataSet={{ exerciseBuilderMediaRemoveBtn: 'true' }}
              hideSectionHeading
              compactSection
            />
          </View>
        ) : null}
        <View style={styles.toggleRow}>
          <TouchableOpacity
            style={[styles.toggleChip, videoSourceMode === 'upload' && styles.toggleChipOn]}
            onPress={() => {
              setVideoSourceMode('upload');
              setDisplayUrl('');
            }}
            disabled={saving}
          >
            <Text
              style={[styles.toggleChipText, videoSourceMode === 'upload' && styles.toggleChipTextOn]}
            >
              Upload video
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.toggleChip, videoSourceMode === 'url' && styles.toggleChipOn]}
            onPress={() => {
              setVideoSourceMode('url');
              clearPendingVideo();
            }}
            disabled={saving}
          >
            <Text
              style={[styles.toggleChipText, videoSourceMode === 'url' && styles.toggleChipTextOn]}
            >
              Display URL
            </Text>
          </TouchableOpacity>
        </View>

        {videoSourceMode === 'upload' ? (
          <>
            <Text style={styles.helpText}>
              Choose a video from your device. It uploads to the club YouTube channel as{' '}
              <Text style={styles.helpTextEmphasis}>Exercise: [name]</Text>.
            </Text>
            {Platform.OS === 'web' ? (
              <>
                {React.createElement('input', {
                  ref: (el) => {
                    webVideoFileInputRef.current = el;
                  },
                  type: 'file',
                  accept: 'video/*',
                  disabled: saving,
                  style: isMobileWeb()
                    ? { display: 'none' }
                    : {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 2,
                        fontSize: 0,
                      },
                  onChange: onWebVideoFileChange,
                  onClick: (ev) => ev.stopPropagation(),
                })}
                {isMobileWeb() ? (
                  <TouchableOpacity
                    style={styles.uploadButton}
                    onPress={onMobileWebPickVideo}
                    activeOpacity={0.8}
                    disabled={saving}
                  >
                    <Text style={styles.uploadButtonText}>Upload from files</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={[styles.uploadButton, styles.webVideoPickButton]}>
                    <Text style={styles.uploadButtonText} pointerEvents="none">
                      Upload from files
                    </Text>
                  </View>
                )}
              </>
            ) : (
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={pickVideoFromDevice}
                activeOpacity={0.8}
                disabled={saving}
              >
                <Text style={styles.uploadButtonText}>Upload from files</Text>
              </TouchableOpacity>
            )}
            <View
              style={styles.pendingFileRow}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderPendingVideoFile: 'true' } }
                : {})}
            >
              <TextInput
                style={styles.readOnlyVideoInput}
                value={pendingVideo?.fileName || ''}
                editable={false}
                placeholder="No video selected"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                numberOfLines={1}
              />
              {pendingVideo ? (
                <TouchableOpacity onPress={clearPendingVideo} hitSlop={12} disabled={saving}>
                  <Text style={styles.pendingFileRemove}>✕</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {videoPickError ? (
              <Text style={styles.videoPickError}>{videoPickError}</Text>
            ) : null}
            {Platform.OS === 'web' && isMobileWeb() && showFilesFallback ? (
              <TouchableOpacity
                style={styles.browseFilesButton}
                onPress={onMobileWebBrowseFiles}
                activeOpacity={0.8}
                disabled={saving}
              >
                <Text style={styles.browseFilesButtonText}>Browse Files</Text>
              </TouchableOpacity>
            ) : null}
          </>
        ) : (
          <>
            <Text style={styles.helpText}>
              Paste a YouTube URL. It is saved on the exercise as the display URL.
            </Text>
            <TextInput
              style={styles.input}
              value={displayUrl}
              onChangeText={setDisplayUrl}
              placeholder="https://www.youtube.com/…"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              editable={!saving}
              autoCapitalize="none"
            />
          </>
        )}

        <View style={styles.assignStudentsBlock}>
          <View style={styles.assignStudentsTitleRow}>
            <Text style={[styles.label, styles.assignStudentsLabel]}>Assign to Students</Text>
            {!loadingAssignStudents && assignStudents.length > 0 && !myExerciseOnly ? (
              <TouchableOpacity
                style={styles.assignSelectAllControl}
                onPress={handleSelectAllAssignStudentsToggle}
                disabled={saving}
                activeOpacity={0.75}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: allAssignStudentsSelected }}
                {...(Platform.OS === 'web'
                  ? { dataSet: { exerciseBuilderAssignSelectAll: 'true' } }
                  : {})}
              >
                <Text
                  style={styles.assignSelectAllLabel}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderAssignSelectAllLabel: 'true' } }
                    : {})}
                >
                  Select all
                </Text>
                <View
                  style={[
                    styles.assignSelectAllCheckbox,
                    allAssignStudentsSelected && styles.assignSelectAllCheckboxSelected,
                  ]}
                >
                  {allAssignStudentsSelected ? (
                    <Text style={styles.assignSelectAllCheckmark}>✓</Text>
                  ) : null}
                </View>
              </TouchableOpacity>
            ) : null}
          </View>
            {loadingAssignStudents ? (
              <ActivityIndicator
                style={styles.assignStudentsLoading}
                color={theme.colors.primary || '#d4af37'}
              />
            ) : (
              <View
                style={styles.pickDropdownWrap}
                {...(Platform.OS === 'web'
                  ? { dataSet: { exerciseBuilderAssignDropdown: 'true' } }
                  : {})}
              >
                <TouchableOpacity
                  style={styles.pickTrigger}
                  onPress={() => setShowAssignStudentsDropdown((open) => !open)}
                  disabled={saving || assignStudents.length === 0}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.pickTriggerText,
                      !selectedAssignStudentsSummary && styles.pickTriggerPlaceholder,
                    ]}
                    numberOfLines={2}
                  >
                    {selectedAssignStudentsSummary ||
                      (assignStudents.length === 0
                        ? 'No students available'
                        : 'Select students to assign')}
                  </Text>
                  {assignStudents.length > 0 ? (
                    <Text style={styles.pickTriggerArrow}>
                      {showAssignStudentsDropdown ? '▲' : '▼'}
                    </Text>
                  ) : null}
                </TouchableOpacity>
                {showAssignStudentsDropdown && assignStudents.length > 0 ? (
                  <ScrollView
                    style={styles.assignStudentsList}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                  >
                    <TouchableOpacity
                      style={[
                        styles.assignMyExerciseOnlyRow,
                        myExerciseOnly && styles.assignMyExerciseOnlyRowSelected,
                      ]}
                      onPress={toggleMyExerciseOnly}
                      disabled={saving}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: myExerciseOnly, disabled: saving }}
                      {...(Platform.OS === 'web'
                        ? {
                            dataSet: {
                              exerciseBuilderAssignMyOnly: 'true',
                              exerciseBuilderAssignMyOnlySelected: myExerciseOnly ? 'true' : 'false',
                            },
                          }
                        : {})}
                    >
                      <Text
                        style={[
                          styles.assignStudentCheckboxMark,
                          myExerciseOnly && styles.assignStudentCheckboxMarkSelected,
                        ]}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { exerciseBuilderAssignCheckboxMark: 'true' } }
                          : {})}
                      >
                        {myExerciseOnly ? '☑' : '☐'}
                      </Text>
                      <Text
                        style={styles.assignMyExerciseOnlyLabel}
                        numberOfLines={1}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { exerciseBuilderAssignMyOnlyLabel: 'true' } }
                          : {})}
                      >
                        My Exercise only
                      </Text>
                    </TouchableOpacity>
                    {assignStudents.map((student) => {
                      const selected = isStudentIdSelected(selectedAssignStudentIds, student.id);
                      const locked = isLockedAssignStudent(student);
                      const disabled = saving || locked || myExerciseOnly;
                      return (
                        <TouchableOpacity
                          key={student.id}
                          style={[
                            styles.assignStudentRow,
                            locked && styles.assignStudentRowLocked,
                            myExerciseOnly && styles.assignStudentRowDisabled,
                          ]}
                          onPress={() => toggleAssignStudent(student)}
                          disabled={disabled}
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: selected, disabled }}
                          {...(Platform.OS === 'web'
                            ? {
                                dataSet: {
                                  exerciseBuilderAssignRow: 'true',
                                  exerciseBuilderAssignRowSelected: selected ? 'true' : 'false',
                                  exerciseBuilderAssignRowDisabled: myExerciseOnly ? 'true' : 'false',
                                },
                              }
                            : {})}
                        >
                          <Text
                            style={[
                              styles.assignStudentCheckboxMark,
                              selected && styles.assignStudentCheckboxMarkSelected,
                            ]}
                            {...(Platform.OS === 'web'
                              ? { dataSet: { exerciseBuilderAssignCheckboxMark: 'true' } }
                              : {})}
                          >
                            {selected ? '☑' : '☐'}
                          </Text>
                          <Text
                            style={styles.assignStudentName}
                            numberOfLines={1}
                            {...(Platform.OS === 'web'
                              ? { dataSet: { exerciseBuilderAssignRowLabel: 'true' } }
                              : {})}
                          >
                            {studentAssignListLabel(student, { locked })}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                ) : null}
              </View>
            )}
          </View>
      </View>
    </>
  );

  return (
    <View
      style={styles.container}
      data-screen="exercise-builder"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View
        style={styles.header}
        {...(Platform.OS === 'web'
          ? { dataSet: { header: 'true', lightMode: !theme.isDarkMode ? 'true' : 'false' } }
          : { 'data-header': 'true', 'data-light-mode': !theme.isDarkMode })}
      >
        <View
          style={styles.hamburgerContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'hamburger',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'hamburger',
              })}
        >
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View
          style={styles.breadcrumbContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'breadcrumb',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'breadcrumb',
              })}
        >
          <Text style={styles.breadcrumb}>Exercise Builder</Text>
        </View>
        <View
          style={styles.profileIconContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'profile',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'profile',
              })}
        >
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        data-scroll-container="exercise-builder"
      >
        {/* Title Section */}
        <View style={styles.titleSection}>
          <Text style={styles.title}>Exercise Builder</Text>
          <Text style={styles.subtitle}>
            Exercises can come from a course or a grade or separately in neither. Example: headstand, course: iron head. 1 legged squat, grade: second degree. knuckle pushups: no course/grade
          </Text>
        </View>

        <View style={styles.pageTabRow}>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'new' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('new')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'new' && styles.pageTabTextOn]}>
              New
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
            onPress={() => switchBuilderTab('edit')}
            disabled={saving}
          >
            <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>
              Edit
            </Text>
          </TouchableOpacity>
        </View>

        {(builderTab === 'new' && !saveStatus) || builderTab === 'edit' ? (
          <View style={styles.pathResetRow}>
            <TouchableOpacity
              style={styles.pathResetButton}
              onPress={handlePageRefresh}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel={
                editingExerciseId != null
                  ? 'Reload exercise from server'
                  : 'Reset path selections'
              }
              {...(Platform.OS === 'web' ? { dataSet: { exerciseBuilderPathReset: 'true' } } : {})}
            >
              <MaterialIcons
                name="refresh"
                size={28}
                color={theme.colors.primary || '#d4af37'}
              />
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Success/Error Status Box */}
        {builderTab === 'new' && saveStatus && (
          <View style={styles.form}>
            <View
              style={[
                styles.statusBox,
                saveStatus === 'success' ? styles.successBox : styles.errorBox,
              ]}
              {...(Platform.OS === 'web'
                ? {
                    dataSet: {
                      exerciseBuilderSaveStatus: 'true',
                      exerciseBuilderSaveStatusType: saveStatus,
                    },
                  }
                : {})}
            >
              <Text
                style={[
                  styles.statusBoxTitle,
                  saveStatus === 'success' && styles.successBoxText,
                  saveStatus === 'error' && styles.errorBoxText,
                ]}
                {...(Platform.OS === 'web'
                  ? { dataSet: { exerciseBuilderSaveStatusText: 'true' } }
                  : {})}
              >
                {saveStatus === 'success' ? 'Record Created Successfully!' : 'Error'}
              </Text>
              {saveStatus === 'error' && errorMessage ? (
                <Text
                  style={styles.errorMessage}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderSaveStatusText: 'true' } }
                    : {})}
                >
                  {errorMessage}
                </Text>
              ) : null}
              {saveStatus === 'success' && (
                <View style={styles.statusButtons}>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.backButton]}
                    onPress={() => navigation.navigate('BuilderMain', { userId: currentUser?.id || 1 })}
                  >
                    <Text style={styles.statusButtonText}>Back to Builders page</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.resetStatusButton]}
                    onPress={handleReset}
                  >
                    <Text style={styles.statusButtonText}>Reset form</Text>
                  </TouchableOpacity>
                </View>
              )}
              {saveStatus === 'error' && (
                <TouchableOpacity
                  style={[styles.statusButton, styles.resetStatusButton]}
                  onPress={() => {
                    setSaveStatus(null);
                    setErrorMessage('');
                  }}
                >
                  <Text style={styles.statusButtonText}>Try Again</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {/* New tab */}
        {builderTab === 'new' && !saveStatus ? (
        <View style={styles.form}>
          <View
            style={[
              styles.fieldContainer,
              styles.pathSectionCourse,
              isNewCourseSectionLocked && styles.selectionSectionLocked,
            ]}
            pointerEvents={isNewCourseSectionLocked ? 'none' : 'auto'}
            {...(Platform.OS === 'web'
              ? { dataSet: { exerciseBuilderPathSection: 'course' } }
              : {})}
          >
            <Text style={styles.label}>Course exercise types</Text>
            <Text style={styles.helpText}>
              If the exercise belongs to a course, select the course and exercise type here.
            </Text>
            <View style={styles.pickDropdownWrap}>
              <TouchableOpacity
                style={styles.pickTrigger}
                onPress={() => setShowCourseDropdown((open) => !open)}
                disabled={saving || creatableCourses.length === 0}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text
                  style={[styles.pickTriggerText, !selectedCourseLabel && styles.pickTriggerPlaceholder]}
                  numberOfLines={1}
                >
                  {selectedCourseLabel ||
                    (creatableCourses.length === 0
                      ? 'No enrolled courses available'
                      : 'Select course')}
                </Text>
                {creatableCourses.length > 0 ? (
                  <Text style={styles.pickTriggerArrow}>{showCourseDropdown ? '▲' : '▼'}</Text>
                ) : null}
              </TouchableOpacity>
              {showCourseDropdown && creatableCourses.length > 0 ? (
                <ScrollView
                  style={styles.pickDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {creatableCourses.map((course) => (
                    <TouchableOpacity
                      key={course.id}
                      style={[
                        styles.dropdownOption,
                        selectedCourseId === course.id && styles.dropdownOptionSelected,
                      ]}
                      onPress={() => handleCourseSelect(course)}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.dropdownOptionText,
                          selectedCourseId === course.id && styles.dropdownOptionTextSelected,
                        ]}
                      >
                        {course.course_name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : null}
            </View>

            <View style={[styles.pickDropdownWrap, styles.pathSectionCourseTypeSlot]}>
              <Text style={[styles.label, { marginBottom: 8 }]}>Exercise type *</Text>
              <TouchableOpacity
                style={[
                  styles.pickTrigger,
                  !selectedCourseId && styles.pickTriggerInactive,
                ]}
                onPress={() => {
                  if (!selectedCourseId) return;
                  setShowCourseTypesDropdown((open) => !open);
                }}
                disabled={
                  saving ||
                  !selectedCourseId ||
                  isNewCourseSectionLocked ||
                  exerciseTypes.length === 0
                }
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text
                  style={[
                    styles.pickTriggerText,
                    (!selectedCourseExerciseTypeLabel || !selectedCourseId) &&
                      styles.pickTriggerPlaceholder,
                  ]}
                  numberOfLines={1}
                >
                  {!selectedCourseId
                    ? 'Select course first'
                    : selectedCourseExerciseTypeLabel ||
                      (exerciseTypes.length === 0
                        ? 'No exercise types linked to this course'
                        : 'Select exercise type')}
                </Text>
                {selectedCourseId && exerciseTypes.length > 0 ? (
                  <Text style={styles.pickTriggerArrow}>
                    {showCourseTypesDropdown ? '▲' : '▼'}
                  </Text>
                ) : null}
              </TouchableOpacity>
              {showCourseTypesDropdown && selectedCourseId && exerciseTypes.length > 0 ? (
                <ScrollView
                  style={styles.pickDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {exerciseTypes.map((exerciseType) => (
                    <TouchableOpacity
                      key={exerciseType.id}
                      style={[
                        styles.dropdownOption,
                        typeSelectionSource === 'course' &&
                          selectedExerciseTypeId === exerciseType.id &&
                          styles.dropdownOptionSelected,
                      ]}
                      onPress={() => handleNewCourseExerciseTypeSelect(exerciseType)}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.dropdownOptionText,
                          typeSelectionSource === 'course' &&
                            selectedExerciseTypeId === exerciseType.id &&
                            styles.dropdownOptionTextSelected,
                        ]}
                      >
                        {exerciseType.exercise_type_name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : null}
            </View>
            <View
              style={[
                styles.createNewContainer,
                styles.pathSectionCourseCreateSlot,
                !selectedCourseId && styles.pathSectionSlotInactive,
              ]}
              pointerEvents={selectedCourseId && !isNewCourseSectionLocked ? 'auto' : 'none'}
            >
              <Text style={styles.createNewText}>
                {exerciseTypes.length > 0
                  ? '*If the exercise type doesn\'t exist, create it here*'
                  : 'This course has no exercise types yet — create one before adding an exercise.'}
              </Text>
              <TouchableOpacity
                style={[
                  styles.createNewButton,
                  exerciseTypes.length === 0 && styles.createNewButtonRed,
                  !selectedCourseId && styles.createNewButtonDisabled,
                ]}
                onPress={() => {
                  navigation.navigate('ExerciseTypeBuilder', {
                    userId: currentUser?.id || 1,
                    returnTo: 'ExerciseBuilder',
                    preselectedCourseId: selectedCourseId,
                  });
                }}
                disabled={!selectedCourseId || saving}
              >
                <Text style={styles.createNewButtonText}>Go to Exercise Type Builder</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View
            style={[
              styles.fieldContainer,
              styles.pathSectionGrade,
              isNewGradeSectionLocked && styles.selectionSectionLocked,
            ]}
            pointerEvents={isNewGradeSectionLocked ? 'none' : 'auto'}
            {...(Platform.OS === 'web'
              ? { dataSet: { exerciseBuilderPathSection: 'grade' } }
              : {})}
          >
            <Text style={styles.label}>Grade exercise types</Text>
            <Text style={styles.helpText}>
              Select a grade to choose an exercise type linked to that grade.
            </Text>
            <View style={styles.pickDropdownWrap}>
              <TouchableOpacity
                style={styles.pickTrigger}
                onPress={() => setShowGradeDropdown((open) => !open)}
                disabled={saving || grades.length === 0}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text
                  style={[styles.pickTriggerText, !selectedGradeLabel && styles.pickTriggerPlaceholder]}
                  numberOfLines={1}
                >
                  {selectedGradeLabel ||
                    (grades.length === 0 ? 'No grades available' : 'Select grade')}
                </Text>
                {grades.length > 0 ? (
                  <Text style={styles.pickTriggerArrow}>{showGradeDropdown ? '▲' : '▼'}</Text>
                ) : null}
              </TouchableOpacity>
              {showGradeDropdown && grades.length > 0 ? (
                <ScrollView
                  style={styles.pickDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {grades.map((grade) => {
                    const gradeDisabled = isGradeBeyondViewerAccess(grade, currentUser);
                    return (
                    <TouchableOpacity
                      key={grade.id}
                      style={[
                        styles.dropdownOption,
                        selectedGradeId === grade.id && styles.dropdownOptionSelected,
                        gradeDisabled && styles.dropdownOptionDisabled,
                      ]}
                      onPress={() => handleNewGradeSelect(grade)}
                      disabled={gradeDisabled}
                      activeOpacity={gradeDisabled ? 1 : 0.7}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.dropdownOptionText,
                          selectedGradeId === grade.id && styles.dropdownOptionTextSelected,
                          gradeDisabled && styles.dropdownOptionTextDisabled,
                        ]}
                      >
                        {grade.grade_name}
                      </Text>
                    </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}
            </View>

            {selectedGradeId &&
            isGradeBeyondViewerAccess(
              grades.find((g) => g.id === selectedGradeId),
              currentUser
            ) ? (
              <Text style={styles.creationAccessHint}>
                You can only create exercises for grades up to one rank above your own.
              </Text>
            ) : null}

            <View style={[styles.pickDropdownWrap, styles.pathSectionGradeTypeSlot]}>
              <Text style={[styles.label, { marginBottom: 8 }]}>Exercise type *</Text>
              <TouchableOpacity
                style={[styles.pickTrigger, !selectedGradeId && styles.pickTriggerInactive]}
                onPress={() => {
                  if (!selectedGradeId) return;
                  setShowGradeTypesDropdown((open) => !open);
                }}
                disabled={
                  saving ||
                  !selectedGradeId ||
                  isNewGradeSectionLocked ||
                  loadingGradeExerciseTypes ||
                  gradeExerciseTypes.length === 0
                }
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text
                  style={[
                    styles.pickTriggerText,
                    (!selectedGradeExerciseTypeLabel || !selectedGradeId) &&
                      styles.pickTriggerPlaceholder,
                  ]}
                  numberOfLines={1}
                >
                  {!selectedGradeId
                    ? 'Select grade first'
                    : loadingGradeExerciseTypes
                      ? 'Loading exercise types…'
                      : selectedGradeExerciseTypeLabel ||
                        (gradeExerciseTypes.length === 0
                          ? 'No exercise types linked to this grade'
                          : 'Select exercise type')}
                </Text>
                {selectedGradeId &&
                !loadingGradeExerciseTypes &&
                gradeExerciseTypes.length > 0 ? (
                  <Text style={styles.pickTriggerArrow}>
                    {showGradeTypesDropdown ? '▲' : '▼'}
                  </Text>
                ) : null}
              </TouchableOpacity>
              {showGradeTypesDropdown &&
              selectedGradeId &&
              !loadingGradeExerciseTypes &&
              gradeExerciseTypes.length > 0 ? (
                <ScrollView
                  style={styles.pickDropdownList}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  {gradeExerciseTypes.map((exerciseType) => (
                    <TouchableOpacity
                      key={`grade-type-${exerciseType.id}`}
                      style={[
                        styles.dropdownOption,
                        typeSelectionSource === 'grade' &&
                          selectedExerciseTypeId === exerciseType.id &&
                          styles.dropdownOptionSelected,
                      ]}
                      onPress={() => handleNewGradeExerciseTypeSelect(exerciseType)}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.dropdownOptionText,
                          typeSelectionSource === 'grade' &&
                            selectedExerciseTypeId === exerciseType.id &&
                            styles.dropdownOptionTextSelected,
                        ]}
                      >
                        {exerciseType.exercise_type_name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              ) : null}
            </View>
          </View>

          <View
            style={[
              styles.fieldContainer,
              styles.pathSectionNonCourse,
              isNewNonCourseSectionLocked && styles.selectionSectionLocked,
            ]}
            pointerEvents={isNewNonCourseSectionLocked ? 'none' : 'auto'}
            {...(Platform.OS === 'web'
              ? { dataSet: { exerciseBuilderPathSection: 'nonCourse' } }
              : {})}
          >
            <Text style={styles.label}>Non course/grade exercise types</Text>
            <Text style={styles.helpText}>
              Exercise types not linked to any course or grade that you have access to.
            </Text>
            {!canCreateNonCourse ? (
              <Text style={styles.creationAccessHint}>
                You must be at least 1st degree black belt to create non-course exercises.
              </Text>
            ) : null}
            {loadingNonCourseTypes ? (
              <ActivityIndicator
                style={styles.exerciseOrderLoading}
                color={theme.colors.primary || '#d4af37'}
              />
            ) : !currentUser?.id ? (
              <Text style={styles.helpText}>Loading user…</Text>
            ) : (
              <View style={styles.pickDropdownWrap}>
                <TouchableOpacity
                  style={styles.pickTrigger}
                  onPress={() => setShowNonCourseTypesDropdown((open) => !open)}
                  disabled={saving || nonCourseExerciseTypes.length === 0}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.pickTriggerText,
                      !selectedNonCourseExerciseTypeLabel && styles.pickTriggerPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {selectedNonCourseExerciseTypeLabel ||
                      (nonCourseExerciseTypes.length === 0
                        ? 'No non-course/grade exercise types available'
                        : 'Select non-course/grade exercise type')}
                  </Text>
                  {nonCourseExerciseTypes.length > 0 ? (
                    <Text style={styles.pickTriggerArrow}>
                      {showNonCourseTypesDropdown ? '▲' : '▼'}
                    </Text>
                  ) : null}
                </TouchableOpacity>
                {showNonCourseTypesDropdown && nonCourseExerciseTypes.length > 0 ? (
                  <ScrollView
                    style={styles.pickDropdownList}
                    nestedScrollEnabled
                    keyboardShouldPersistTaps="handled"
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                  >
                    {nonCourseExerciseTypes.map((exerciseType) => (
                      <TouchableOpacity
                        key={`non-course-${exerciseType.id}`}
                        style={[
                          styles.dropdownOption,
                          typeSelectionSource === 'nonCourse' &&
                            selectedExerciseTypeId === exerciseType.id &&
                            styles.dropdownOptionSelected,
                        ]}
                        onPress={() => handleNewNonCourseExerciseTypeSelect(exerciseType)}
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                      >
                        <Text
                          style={[
                            styles.dropdownOptionText,
                            typeSelectionSource === 'nonCourse' &&
                              selectedExerciseTypeId === exerciseType.id &&
                              styles.dropdownOptionTextSelected,
                          ]}
                        >
                          {exerciseType.exercise_type_name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                ) : null}
              </View>
            )}
          </View>

          {renderExerciseFormFields()}

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.resetButton]}
              onPress={handleReset}
              disabled={saving}
            >
              <Text style={styles.buttonText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.saveButton]}
              onPress={() => void handleSave()}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#000" />
              ) : (
                <Text style={styles.buttonText}>Save</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
        ) : null}

        {/* Edit tab */}
        {builderTab === 'edit' ? (
          <View style={styles.form}>
            {!editingExerciseId ? (
              <>
                <View
                  style={[
                    styles.fieldContainer,
                    styles.pathSectionCourse,
                    isEditCourseSectionLocked && styles.selectionSectionLocked,
                  ]}
                  pointerEvents={isEditCourseSectionLocked ? 'none' : 'auto'}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderPathSection: 'course' } }
                    : {})}
                >
                  <Text style={styles.label}>Course exercise types</Text>
                  <Text style={styles.helpText}>Select a course to find exercises to edit.</Text>
                  <View style={styles.pickDropdownWrap}>
                    <TouchableOpacity
                      style={styles.pickTrigger}
                      onPress={() => setShowEditCourseDropdown((open) => !open)}
                      disabled={saving || courses.length === 0}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.pickTriggerText,
                          !selectedEditCourseLabel && styles.pickTriggerPlaceholder,
                        ]}
                        numberOfLines={1}
                      >
                        {selectedEditCourseLabel ||
                          (courses.length === 0 ? 'No courses available' : 'Select course')}
                      </Text>
                      {courses.length > 0 ? (
                        <Text style={styles.pickTriggerArrow}>
                          {showEditCourseDropdown ? '▲' : '▼'}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                    {showEditCourseDropdown && courses.length > 0 ? (
                      <ScrollView
                        style={styles.pickDropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {courses.map((course) => (
                          <TouchableOpacity
                            key={course.id}
                            style={[
                              styles.dropdownOption,
                              editCourseId === course.id && styles.dropdownOptionSelected,
                            ]}
                            onPress={() => handleEditCourseSelect(course)}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                editCourseId === course.id && styles.dropdownOptionTextSelected,
                              ]}
                            >
                              {course.course_name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : null}
                  </View>

                  <View style={[styles.pickDropdownWrap, styles.pathSectionCourseTypeSlot]}>
                    <Text style={[styles.label, { marginBottom: 8 }]}>Exercise type</Text>
                    <TouchableOpacity
                      style={[
                        styles.pickTrigger,
                        !editCourseId && styles.pickTriggerInactive,
                      ]}
                      onPress={() => {
                        if (!editCourseId) return;
                        setShowEditCourseTypesDropdown((open) => !open);
                      }}
                      disabled={
                        saving ||
                        !editCourseId ||
                        isEditCourseSectionLocked ||
                        editExerciseTypes.length === 0
                      }
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.pickTriggerText,
                          (!selectedEditCourseExerciseTypeLabel || !editCourseId) &&
                            styles.pickTriggerPlaceholder,
                        ]}
                        numberOfLines={1}
                      >
                        {!editCourseId
                          ? 'Select course first'
                          : selectedEditCourseExerciseTypeLabel ||
                            (editExerciseTypes.length === 0
                              ? 'No exercise types linked to this course'
                              : 'Select exercise type')}
                      </Text>
                      {editCourseId && editExerciseTypes.length > 0 ? (
                        <Text style={styles.pickTriggerArrow}>
                          {showEditCourseTypesDropdown ? '▲' : '▼'}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                    {showEditCourseTypesDropdown && editCourseId && editExerciseTypes.length > 0 ? (
                      <ScrollView
                        style={styles.pickDropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {editExerciseTypes.map((exerciseType) => (
                          <TouchableOpacity
                            key={exerciseType.id}
                            style={[
                              styles.dropdownOption,
                              editSelectionSource === 'course' &&
                                editExerciseTypeId === exerciseType.id &&
                                styles.dropdownOptionSelected,
                            ]}
                            onPress={() => handleEditExerciseTypeSelect(exerciseType)}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                editSelectionSource === 'course' &&
                                  editExerciseTypeId === exerciseType.id &&
                                  styles.dropdownOptionTextSelected,
                              ]}
                            >
                              {exerciseType.exercise_type_name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : null}
                  </View>
                </View>

                <View
                  style={[
                    styles.fieldContainer,
                    styles.pathSectionGrade,
                    isEditGradeSectionLocked && styles.selectionSectionLocked,
                  ]}
                  pointerEvents={isEditGradeSectionLocked ? 'none' : 'auto'}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderPathSection: 'grade' } }
                    : {})}
                >
                  <Text style={styles.label}>Grade exercise types</Text>
                  <Text style={styles.helpText}>
                    Select a grade to view and edit exercises linked to that grade.
                  </Text>
                  <View style={styles.pickDropdownWrap}>
                    <TouchableOpacity
                      style={styles.pickTrigger}
                      onPress={() => setShowEditGradeDropdown((open) => !open)}
                      disabled={saving || grades.length === 0}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.pickTriggerText,
                          !selectedEditGradeLabel && styles.pickTriggerPlaceholder,
                        ]}
                        numberOfLines={1}
                      >
                        {selectedEditGradeLabel ||
                          (grades.length === 0 ? 'No grades available' : 'Select grade')}
                      </Text>
                      {grades.length > 0 ? (
                        <Text style={styles.pickTriggerArrow}>
                          {showEditGradeDropdown ? '▲' : '▼'}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                    {showEditGradeDropdown && grades.length > 0 ? (
                      <ScrollView
                        style={styles.pickDropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {grades.map((grade) => (
                          <TouchableOpacity
                            key={grade.id}
                            style={[
                              styles.dropdownOption,
                              editGradeId === grade.id && styles.dropdownOptionSelected,
                            ]}
                            onPress={() => handleEditGradeSelect(grade)}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                editGradeId === grade.id && styles.dropdownOptionTextSelected,
                              ]}
                            >
                              {grade.grade_name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : null}
                  </View>

                  <View style={[styles.pickDropdownWrap, styles.pathSectionGradeTypeSlot]}>
                    <Text style={[styles.label, { marginBottom: 8 }]}>Exercise type</Text>
                    <TouchableOpacity
                      style={[
                        styles.pickTrigger,
                        !editGradeId && styles.pickTriggerInactive,
                      ]}
                      onPress={() => {
                        if (!editGradeId) return;
                        setShowEditGradeTypesDropdown((open) => !open);
                      }}
                      disabled={
                        saving ||
                        !editGradeId ||
                        isEditGradeSectionLocked ||
                        loadingEditGradeExerciseTypes ||
                        editGradeExerciseTypes.length === 0
                      }
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                    >
                      <Text
                        style={[
                          styles.pickTriggerText,
                          (!selectedEditGradeExerciseTypeLabel || !editGradeId) &&
                            styles.pickTriggerPlaceholder,
                        ]}
                        numberOfLines={1}
                      >
                        {!editGradeId
                          ? 'Select grade first'
                          : loadingEditGradeExerciseTypes
                            ? 'Loading exercise types…'
                            : selectedEditGradeExerciseTypeLabel ||
                              (editGradeExerciseTypes.length === 0
                                ? 'No exercise types linked to this grade'
                                : 'Select exercise type')}
                      </Text>
                      {editGradeId &&
                      !loadingEditGradeExerciseTypes &&
                      editGradeExerciseTypes.length > 0 ? (
                        <Text style={styles.pickTriggerArrow}>
                          {showEditGradeTypesDropdown ? '▲' : '▼'}
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                    {showEditGradeTypesDropdown &&
                    editGradeId &&
                    !loadingEditGradeExerciseTypes &&
                    editGradeExerciseTypes.length > 0 ? (
                      <ScrollView
                        style={styles.pickDropdownList}
                        nestedScrollEnabled
                        keyboardShouldPersistTaps="handled"
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                      >
                        {editGradeExerciseTypes.map((exerciseType) => (
                          <TouchableOpacity
                            key={`grade-type-${exerciseType.id}`}
                            style={[
                              styles.dropdownOption,
                              editSelectionSource === 'grade' &&
                                editExerciseTypeId === exerciseType.id &&
                                styles.dropdownOptionSelected,
                            ]}
                            onPress={() => handleEditGradeExerciseTypeSelect(exerciseType)}
                            {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                          >
                            <Text
                              style={[
                                styles.dropdownOptionText,
                                editSelectionSource === 'grade' &&
                                  editExerciseTypeId === exerciseType.id &&
                                  styles.dropdownOptionTextSelected,
                              ]}
                            >
                              {exerciseType.exercise_type_name}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    ) : null}
                  </View>
                </View>

                <View
                  style={[
                    styles.fieldContainer,
                    styles.pathSectionNonCourse,
                    isEditNonCourseSectionLocked && styles.selectionSectionLocked,
                  ]}
                  pointerEvents={isEditNonCourseSectionLocked ? 'none' : 'auto'}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { exerciseBuilderPathSection: 'nonCourse' } }
                    : {})}
                >
                  <Text style={styles.label}>Non course/grade exercise types</Text>
                  <Text style={styles.helpText}>
                    Exercise types not linked to any course or grade that you have access to.
                  </Text>
                  {loadingEditNonCourseTypes ? (
                    <ActivityIndicator
                      style={styles.exerciseOrderLoading}
                      color={theme.colors.primary || '#d4af37'}
                    />
                  ) : !currentUser?.id ? (
                    <Text style={styles.helpText}>Loading user…</Text>
                  ) : (
                    <View style={styles.pickDropdownWrap}>
                      <TouchableOpacity
                        style={styles.pickTrigger}
                        onPress={() => setShowEditNonCourseTypesDropdown((open) => !open)}
                        disabled={saving || editNonCourseExerciseTypes.length === 0}
                        {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                      >
                        <Text
                          style={[
                            styles.pickTriggerText,
                            !selectedEditNonCourseExerciseTypeLabel && styles.pickTriggerPlaceholder,
                          ]}
                          numberOfLines={1}
                        >
                          {selectedEditNonCourseExerciseTypeLabel ||
                            (editNonCourseExerciseTypes.length === 0
                              ? 'No non-course/grade exercise types available'
                              : 'Select non-course/grade exercise type')}
                        </Text>
                        {editNonCourseExerciseTypes.length > 0 ? (
                          <Text style={styles.pickTriggerArrow}>
                            {showEditNonCourseTypesDropdown ? '▲' : '▼'}
                          </Text>
                        ) : null}
                      </TouchableOpacity>
                      {showEditNonCourseTypesDropdown && editNonCourseExerciseTypes.length > 0 ? (
                        <ScrollView
                          style={styles.pickDropdownList}
                          nestedScrollEnabled
                          keyboardShouldPersistTaps="handled"
                          {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                        >
                          {editNonCourseExerciseTypes.map((exerciseType) => (
                            <TouchableOpacity
                              key={`non-course-${exerciseType.id}`}
                              style={[
                                styles.dropdownOption,
                                editSelectionSource === 'nonCourse' &&
                                  editExerciseTypeId === exerciseType.id &&
                                  styles.dropdownOptionSelected,
                              ]}
                              onPress={() => handleEditNonCourseExerciseTypeSelect(exerciseType)}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  editSelectionSource === 'nonCourse' &&
                                    editExerciseTypeId === exerciseType.id &&
                                    styles.dropdownOptionTextSelected,
                                ]}
                              >
                                {exerciseType.exercise_type_name}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      ) : null}
                    </View>
                  )}
                </View>

                {editExerciseTypeId ? (
                  <View style={styles.fieldContainer}>
                    <View style={styles.exerciseListHeaderRow}>
                      <View style={styles.exerciseListHeaderTextCol}>
                        <Text style={styles.label}>Exercises List</Text>
                        <Text style={styles.helpText}>
                          {moveExerciseMode
                            ? 'Select one or more exercises, then choose a destination type below.'
                            : 'Tap an exercise to edit. Name and description are shown for each.'}
                        </Text>
                      </View>
                      <View
                        style={styles.moveExerciseToggleWrap}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { exerciseBuilderMoveToggle: 'true' } }
                          : {})}
                      >
                        <Text style={styles.moveExerciseToggleLabel}>Move Exercise</Text>
                        <View
                          style={styles.segmentedToggle}
                          accessibilityRole="tablist"
                          accessibilityLabel="Move Exercise mode"
                          {...(Platform.OS === 'web'
                            ? { dataSet: { exerciseBuilderMoveToggleTrack: 'true' } }
                            : {})}
                        >
                          <TouchableOpacity
                            style={[
                              styles.segmentedToggleOption,
                              !moveExerciseMode && styles.segmentedToggleOptionActiveOff,
                            ]}
                            onPress={() => handleMoveExerciseModeToggle(false)}
                            disabled={loadingEditExercises || movingExercises}
                            accessibilityRole="tab"
                            accessibilityState={{ selected: !moveExerciseMode }}
                            accessibilityLabel="Move Exercise off"
                            activeOpacity={0.85}
                            {...(Platform.OS === 'web'
                              ? {
                                  dataSet: {
                                    exerciseBuilderMoveToggleOption: 'off',
                                    exerciseBuilderMoveToggleActive: !moveExerciseMode
                                      ? 'true'
                                      : 'false',
                                  },
                                }
                              : {})}
                          >
                            <Text
                              style={[
                                styles.segmentedToggleText,
                                !moveExerciseMode && styles.segmentedToggleTextActive,
                              ]}
                            >
                              OFF
                            </Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[
                              styles.segmentedToggleOption,
                              moveExerciseMode && styles.segmentedToggleOptionActiveOn,
                            ]}
                            onPress={() => handleMoveExerciseModeToggle(true)}
                            disabled={loadingEditExercises || movingExercises}
                            accessibilityRole="tab"
                            accessibilityState={{ selected: moveExerciseMode }}
                            accessibilityLabel="Move Exercise on"
                            activeOpacity={0.85}
                            {...(Platform.OS === 'web'
                              ? {
                                  dataSet: {
                                    exerciseBuilderMoveToggleOption: 'on',
                                    exerciseBuilderMoveToggleActive: moveExerciseMode
                                      ? 'true'
                                      : 'false',
                                  },
                                }
                              : {})}
                          >
                            <Text
                              style={[
                                styles.segmentedToggleText,
                                moveExerciseMode && styles.segmentedToggleTextActive,
                              ]}
                            >
                              ON
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                    {loadingEditExercises ? (
                      <ActivityIndicator
                        style={styles.exerciseOrderLoading}
                        color={theme.colors.primary || '#d4af37'}
                      />
                    ) : editExercises.length === 0 ? (
                      <Text style={styles.helpText}>No exercises in this type yet.</Text>
                    ) : (
                      <>
                        <View
                          style={styles.exercisePickList}
                          {...(Platform.OS === 'web'
                            ? { dataSet: { exerciseBuilderPickList: 'true' } }
                            : {})}
                        >
                          {editExercises.map((ex) => {
                            const isMoveSelected =
                              moveExerciseMode && moveSelectedExerciseIds.has(ex.id);
                            const deleteMenuOpen =
                              !moveExerciseMode && openExerciseDeleteMenuId === ex.id;
                            return (
                              <View
                                key={ex.id}
                                style={[
                                  styles.exercisePickCard,
                                  isMoveSelected && styles.exercisePickCardSelected,
                                  deleteMenuOpen && styles.exercisePickCardMenuOpen,
                                ]}
                                {...(Platform.OS === 'web'
                                  ? {
                                      dataSet: {
                                        exerciseBuilderPickCard: 'true',
                                        exerciseBuilderPickCardSelected: isMoveSelected
                                          ? 'true'
                                          : 'false',
                                      },
                                    }
                                  : {})}
                              >
                                {isMoveSelected ? (
                                  <View style={styles.exercisePickYellowOverlay} />
                                ) : null}
                                <View style={styles.exercisePickCardInner}>
                                  <TouchableOpacity
                                    style={styles.exercisePickCardMain}
                                    onPress={() => {
                                      setOpenExerciseDeleteMenuId(null);
                                      handleMoveExerciseListPress(ex);
                                    }}
                                    activeOpacity={0.75}
                                  >
                                    <View style={styles.exercisePickCardContent}>
                                      {!moveExerciseMode ? (
                                        <View style={styles.exercisePickNameRow}>
                                          <MaterialIcons
                                            name="edit"
                                            size={20}
                                            color={
                                              theme.colors.heading ||
                                              theme.colors.primary ||
                                              '#d4af37'
                                            }
                                            style={styles.exercisePickEditIcon}
                                          />
                                          <Text style={styles.exercisePickName}>
                                            {ex.exercise_name} [{ex.exercise_order ?? 0}]
                                          </Text>
                                        </View>
                                      ) : (
                                        <View style={styles.exercisePickNameRow}>
                                          <Text
                                            style={[
                                              styles.exercisePickName,
                                              isMoveSelected && styles.exercisePickNameSelected,
                                            ]}
                                          >
                                            {ex.exercise_name} [{ex.exercise_order ?? 0}]
                                          </Text>
                                        </View>
                                      )}
                                      {ex.description ? (
                                        <Text
                                          style={[
                                            styles.exercisePickDesc,
                                            isMoveSelected && styles.exercisePickDescSelected,
                                          ]}
                                          numberOfLines={4}
                                        >
                                          {ex.description}
                                        </Text>
                                      ) : (
                                        <Text
                                          style={[
                                            styles.exercisePickDescMuted,
                                            isMoveSelected && styles.exercisePickDescSelected,
                                          ]}
                                        >
                                          No description
                                        </Text>
                                      )}
                                    </View>
                                  </TouchableOpacity>
                                  {!moveExerciseMode ? (
                                    <View style={styles.cardMenuWrap}>
                                      <TouchableOpacity
                                        style={styles.cardMenuButton}
                                        onPress={() =>
                                          setOpenExerciseDeleteMenuId((current) =>
                                            current === ex.id ? null : ex.id
                                          )
                                        }
                                        disabled={saving || deletingExercise}
                                        accessibilityRole="button"
                                        accessibilityLabel={`Actions for ${ex.exercise_name}`}
                                        {...(Platform.OS === 'web'
                                          ? { dataSet: { exerciseBuilderCardMenuBtn: 'true' } }
                                          : {})}
                                      >
                                        <MaterialIcons
                                          name="more-vert"
                                          size={22}
                                          color={theme.colors.text}
                                        />
                                      </TouchableOpacity>
                                      {deleteMenuOpen ? (
                                        <View
                                          style={styles.cardDeleteMenu}
                                          {...(Platform.OS === 'web'
                                            ? { dataSet: { exerciseBuilderCardMenu: 'true' } }
                                            : {})}
                                        >
                                          <TouchableOpacity
                                            style={styles.cardDeleteMenuItem}
                                            onPress={() => openExerciseDeleteConfirm(ex)}
                                            {...(Platform.OS === 'web'
                                              ? { dataSet: { exerciseBuilderCardMenuItem: 'true' } }
                                              : {})}
                                          >
                                            <Text
                                              style={styles.cardDeleteMenuItemText}
                                              {...(Platform.OS === 'web'
                                                ? {
                                                    dataSet: {
                                                      exerciseBuilderCardMenuText: 'true',
                                                    },
                                                  }
                                                : {})}
                                            >
                                              Delete
                                            </Text>
                                          </TouchableOpacity>
                                        </View>
                                      ) : null}
                                    </View>
                                  ) : null}
                                </View>
                              </View>
                            );
                          })}
                        </View>

                        {moveExerciseMode ? (
                          <View style={styles.moveExercisePanel}>
                            <MovePickDropdown
                              styles={styles}
                              triggerText={
                                moveSelectedExerciseIds.size === 0
                                  ? 'Please select a exercise to move first'
                                  : selectedSameContextMoveTypeName
                                    ? `Move exercise to another Exercise Type… ${selectedSameContextMoveTypeName}`
                                    : 'Move exercise to another Exercise Type…'
                              }
                              isPlaceholder={
                                moveSelectedExerciseIds.size === 0 || !selectedSameContextMoveTypeName
                              }
                              disabled={moveSelectedExerciseIds.size === 0 || movingExercises}
                              open={
                                showMoveTypeDropdown && moveSelectedExerciseIds.size > 0
                              }
                              onToggle={() => {
                                if (moveSelectedExerciseIds.size === 0) return;
                                toggleMoveDropdown('same');
                              }}
                              hasArrow={moveSelectedExerciseIds.size > 0}
                            >
                              <MovePickOptions
                                styles={styles}
                                items={moveTargetExerciseTypes}
                                selectedId={moveDestKind === 'same' ? moveTargetTypeId : null}
                                currentId={null}
                                currentSuffix=""
                                getName={(t) => t.exercise_type_name}
                                onSelect={handleMoveTargetTypeSelect}
                                emptyText={
                                  editSelectionSource === 'course'
                                    ? 'No other exercise types in this course.'
                                    : editSelectionSource === 'grade'
                                      ? 'No other exercise types in this grade.'
                                      : 'No other non-course/grade types available.'
                                }
                              />
                            </MovePickDropdown>

                            {moveSelectedExerciseIds.size > 0 ? (
                              <>
                                <Text
                                  style={styles.moveExerciseOrLabel}
                                  {...webDataSet({ exerciseBuilderMoveOr: 'true' })}
                                >
                                  Or
                                </Text>
                                <Text
                                  style={styles.moveDestSectionLabel}
                                  {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
                                >
                                  Courses
                                </Text>
                                <MovePickDropdown
                                  styles={styles}
                                  triggerText={
                                    selectedMoveDestCourseName
                                      ? `Move exercise to another Course… ${selectedMoveDestCourseName}`
                                      : 'Move exercise to another Course…'
                                  }
                                  isPlaceholder={!selectedMoveDestCourseName}
                                  disabled={movingExercises || courses.length === 0}
                                  open={showMoveDestCourseDropdown}
                                  onToggle={() => toggleMoveDropdown('course')}
                                  hasArrow={courses.length > 0}
                                >
                                  <MovePickOptions
                                    styles={styles}
                                    items={courses}
                                    selectedId={moveDestKind === 'course' ? moveDestCourseId : null}
                                    currentId={currentMoveCourseId}
                                    currentSuffix="current course"
                                    getName={(c) => c.course_name}
                                    onSelect={handleMoveDestCourseSelect}
                                    emptyText="No courses available."
                                  />
                                </MovePickDropdown>
                                {moveDestKind === 'course' && moveDestCourseId != null ? (
                                  <View style={styles.moveDestNestedWrap}>
                                    <MovePickDropdown
                                      styles={styles}
                                      triggerText={
                                        loadingMoveDestCourseTypes
                                          ? 'Loading exercise types…'
                                          : selectedMoveDestCourseTypeName ||
                                            (moveDestCourseTypes.length === 0
                                              ? 'No exercise types in this course'
                                              : 'Select exercise type')
                                      }
                                      isPlaceholder={!selectedMoveDestCourseTypeName}
                                      disabled={movingExercises}
                                      open={showMoveDestCourseTypeDropdown}
                                      onToggle={() => toggleMoveDropdown('courseType')}
                                      hasArrow
                                    >
                                      <MovePickOptions
                                        styles={styles}
                                        items={moveDestCourseTypes}
                                        selectedId={
                                          moveDestKind === 'course' ? moveTargetTypeId : null
                                        }
                                        currentId={editExerciseTypeId}
                                        currentSuffix="current exercise type"
                                        getName={(t) => t.exercise_type_name}
                                        onSelect={handleMoveDestCourseTypeSelect}
                                        emptyText="No exercise types in this course."
                                        loading={loadingMoveDestCourseTypes}
                                        loadingText="Loading exercise types…"
                                      />
                                    </MovePickDropdown>
                                  </View>
                                ) : null}

                                <Text
                                  style={styles.moveExerciseOrLabel}
                                  {...webDataSet({ exerciseBuilderMoveOr: 'true' })}
                                >
                                  Or
                                </Text>
                                <Text
                                  style={styles.moveDestSectionLabel}
                                  {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
                                >
                                  Grade exercises
                                </Text>
                                <MovePickDropdown
                                  styles={styles}
                                  triggerText={
                                    selectedMoveDestGradeName
                                      ? `Move exercise to a grade exercise… ${selectedMoveDestGradeName}`
                                      : 'Move exercise to a grade exercise…'
                                  }
                                  isPlaceholder={!selectedMoveDestGradeName}
                                  disabled={movingExercises || grades.length === 0}
                                  open={showMoveDestGradeDropdown}
                                  onToggle={() => toggleMoveDropdown('grade')}
                                  hasArrow={grades.length > 0}
                                >
                                  <MovePickOptions
                                    styles={styles}
                                    items={grades}
                                    selectedId={moveDestKind === 'grade' ? moveDestGradeId : null}
                                    currentId={currentMoveGradeId}
                                    currentSuffix="current grade"
                                    getName={(g) => g.grade_name}
                                    onSelect={handleMoveDestGradeSelect}
                                    emptyText="No grades available."
                                  />
                                </MovePickDropdown>
                                {moveDestKind === 'grade' && moveDestGradeId != null ? (
                                  <View style={styles.moveDestNestedWrap}>
                                    <MovePickDropdown
                                      styles={styles}
                                      triggerText={
                                        loadingMoveDestGradeTypes
                                          ? 'Loading exercise types…'
                                          : selectedMoveDestGradeTypeName ||
                                            (moveDestGradeTypes.length === 0
                                              ? 'No exercise types in this grade'
                                              : 'Select exercise type')
                                      }
                                      isPlaceholder={!selectedMoveDestGradeTypeName}
                                      disabled={movingExercises}
                                      open={showMoveDestGradeTypeDropdown}
                                      onToggle={() => toggleMoveDropdown('gradeType')}
                                      hasArrow
                                    >
                                      <MovePickOptions
                                        styles={styles}
                                        items={moveDestGradeTypes}
                                        selectedId={
                                          moveDestKind === 'grade' ? moveTargetTypeId : null
                                        }
                                        currentId={editExerciseTypeId}
                                        currentSuffix="current exercise type"
                                        getName={(t) => t.exercise_type_name}
                                        onSelect={handleMoveDestGradeTypeSelect}
                                        emptyText="No exercise types in this grade."
                                        loading={loadingMoveDestGradeTypes}
                                        loadingText="Loading exercise types…"
                                      />
                                    </MovePickDropdown>
                                  </View>
                                ) : null}

                                <Text
                                  style={styles.moveExerciseOrLabel}
                                  {...webDataSet({ exerciseBuilderMoveOr: 'true' })}
                                >
                                  Or
                                </Text>
                                <Text
                                  style={styles.moveDestSectionLabel}
                                  {...webDataSet({ exerciseBuilderMoveDestText: 'true' })}
                                >
                                  Non Course/Grade exercises
                                </Text>
                                <MovePickDropdown
                                  styles={styles}
                                  triggerText={
                                    selectedMoveDestNonCourseTypeName
                                      ? `Move exercise to a Non Course/Grade exercise… ${selectedMoveDestNonCourseTypeName}`
                                      : 'Move exercise to a Non Course/Grade exercise…'
                                  }
                                  isPlaceholder={!selectedMoveDestNonCourseTypeName}
                                  disabled={
                                    movingExercises || editNonCourseExerciseTypes.length === 0
                                  }
                                  open={showMoveDestNonCourseDropdown}
                                  onToggle={() => toggleMoveDropdown('nonCourse')}
                                  hasArrow={editNonCourseExerciseTypes.length > 0}
                                >
                                  <MovePickOptions
                                    styles={styles}
                                    items={editNonCourseExerciseTypes}
                                    selectedId={
                                      moveDestKind === 'nonCourse' ? moveTargetTypeId : null
                                    }
                                    currentId={currentMoveExerciseTypeId}
                                    currentSuffix="current exercise type"
                                    getName={(t) => t.exercise_type_name}
                                    onSelect={handleMoveDestNonCourseTypeSelect}
                                    emptyText="No non-course/grade exercise types available."
                                  />
                                </MovePickDropdown>
                              </>
                            ) : null}

                            <View style={styles.buttonContainer}>
                              <TouchableOpacity
                                style={[styles.button, styles.resetButton]}
                                onPress={handleMoveExercisesCancel}
                                disabled={movingExercises}
                              >
                                <Text style={styles.buttonText}>Cancel</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={[
                                  styles.button,
                                  styles.saveButton,
                                  !canSubmitMoveExercises && styles.saveButtonDisabled,
                                ]}
                                onPress={() => void handleMoveExercisesUpdate()}
                                disabled={!canSubmitMoveExercises}
                              >
                                {movingExercises ? (
                                  <ActivityIndicator color="#000" />
                                ) : (
                                  <Text style={styles.buttonText}>Update</Text>
                                )}
                              </TouchableOpacity>
                            </View>
                          </View>
                        ) : null}
                      </>
                    )}
                  </View>
                ) : null}
              </>
            ) : (
              <>
                <View style={styles.editFormHeaderRow}>
                  <Text style={[styles.editFormHint, styles.editFormHintInHeader]}>
                    Editing exercise in {selectedEditExerciseTypeName}. Cancel returns to the
                    exercise list.
                  </Text>
                  <TouchableOpacity
                    style={styles.editFormDeleteBtn}
                    onPress={() => {
                      const current = editExercises.find((e) => e.id === editingExerciseId);
                      openExerciseDeleteConfirm({
                        id: editingExerciseId,
                        exercise_name:
                          current?.exercise_name || exerciseName || 'this exercise',
                      });
                    }}
                    disabled={saving || deletingExercise}
                    accessibilityRole="button"
                    accessibilityLabel="Delete exercise"
                    {...(Platform.OS === 'web'
                      ? { dataSet: { exerciseBuilderEditDelete: 'true' } }
                      : {})}
                  >
                    <MaterialIcons name="delete-outline" size={20} color="#c62828" />
                    <Text style={styles.editFormDeleteBtnText}>Delete</Text>
                  </TouchableOpacity>
                </View>
                {renderExerciseFormFields()}
                <View style={styles.buttonContainer}>
                  <TouchableOpacity
                    style={[styles.button, styles.resetButton]}
                    onPress={handleEditCancel}
                    disabled={saving}
                  >
                    <Text style={styles.buttonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.button, styles.saveButton]}
                    onPress={() => void handleUpdate()}
                    disabled={saving}
                  >
                    {saving ? (
                      <ActivityIndicator color="#000" />
                    ) : (
                      <Text style={styles.buttonText}>Update</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        ) : null}

        {builderTab === 'edit' && editUpdateToast ? (
          <View
            style={[
              styles.editUpdateToast,
              editUpdateToast.type === 'error'
                ? styles.editUpdateToastError
                : styles.editUpdateToastSuccess,
            ]}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    exerciseBuilderEditToast: 'true',
                    exerciseBuilderEditToastType: editUpdateToast.type,
                  },
                }
              : {})}
          >
            <Text
              style={styles.editUpdateToastMessage}
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderEditToastText: 'true' } }
                : {})}
            >
              {editUpdateToast.message}
            </Text>
            <TouchableOpacity
              onPress={() => setEditUpdateToast(null)}
              style={styles.editUpdateToastClose}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              {...(Platform.OS === 'web'
                ? { dataSet: { exerciseBuilderEditToastClose: 'true' } }
                : {})}
            >
              <MaterialIcons name="close" size={22} color="#ffffff" />
            </TouchableOpacity>
          </View>
        ) : null}
      </ScrollView>
      <Modal
        visible={!!exerciseVideoModal}
        animationType="fade"
        transparent
        onRequestClose={closeExerciseVideoModal}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={styles.ytModalRoot}>
          <Pressable style={styles.ytModalBackdrop} onPress={closeExerciseVideoModal} />
          <View
            style={[
              styles.ytModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#111',
                borderColor: theme.isDarkMode ? theme.colors.border : '#333',
              },
            ]}
          >
            <View style={styles.ytModalHeader}>
              <Text style={[styles.ytModalTitle, { color: theme.colors.heading }]} numberOfLines={2}>
                {exerciseVideoModal?.title ?? 'Video'}
              </Text>
              <TouchableOpacity
                onPress={closeExerciseVideoModal}
                style={styles.ytModalClose}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="Close video"
              >
                <Text
                  style={[
                    styles.ytModalCloseText,
                    { color: theme.isDarkMode ? theme.colors.text : '#ffffff' },
                  ]}
                >
                  ✕
                </Text>
              </TouchableOpacity>
            </View>
            <View style={styles.ytWebViewWrap}>
              {exerciseVideoModal?.uri ? (
                exerciseVideoModal.isImage ? (
                  <Image
                    source={{ uri: exerciseVideoModal.uri }}
                    style={styles.ytModalImage}
                    contentFit="contain"
                    transition={0}
                  />
                ) : (
                  <ModalVideoEmbed
                    uri={exerciseVideoModal.uri}
                    title={exerciseVideoModal.title}
                    webViewStyle={styles.ytWebView}
                  />
                )
              ) : null}
            </View>
          </View>
        </View>
      </Modal>

      <BrandedConfirmModal
        visible={!!exerciseDeleteTarget}
        title="Delete exercise?"
        message={
          exerciseDeleteTarget
            ? `This will permanently delete “${exerciseDeleteTarget.name}”. This cannot be undone.`
            : ''
        }
        confirmLabel="OK"
        cancelLabel="Cancel"
        destructive
        typedConfirmWord="delete"
        typedValue={exerciseDeleteTypedValue}
        onTypedValueChange={setExerciseDeleteTypedValue}
        typedPlaceholder="Type delete to confirm"
        busy={deletingExercise}
        errorMessage={exerciseDeleteError}
        onConfirm={() => void handleDeleteExerciseConfirm()}
        onCancel={dismissExerciseDeleteConfirm}
      />

      <BrandedConfirmModal
        visible={!!removeMediaConfirm}
        title={removeMediaConfirm?.kind === 'video' ? 'Remove video' : 'Remove image'}
        message={
          removeMediaConfirm
            ? `Are you sure you want to remove the ${removeMediaConfirm.kind} for this exercise?`
            : ''
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleRemoveMediaConfirm}
        onCancel={dismissRemoveMediaConfirm}
      />

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function mergeExerciseBuilderStyles(theme, slideWidth, slideHeight) {
  const carouselStyles = createCourseMediaCarouselStyles(theme, slideWidth, slideHeight);
  return {
    ...StyleSheet.flatten(getStyles(theme)),
    ...carouselStyles,
    extCarouselOuter: {
      marginBottom: 4,
      alignSelf: 'stretch',
      width: '100%',
    },
    extThumbStrip: {
      marginTop: 12,
      width: '100%',
      alignSelf: 'stretch',
    },
    extThumbStripContent: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: '100%',
      paddingVertical: 8,
      paddingHorizontal: 4,
    },
    extThumbTile: {
      width: EXERCISE_VIDEO_EXT_THUMB_TILE_WIDTH,
      marginHorizontal: EXERCISE_VIDEO_EXT_THUMB_TILE_GAP / 2,
      alignItems: 'center',
    },
    extThumbTileFrame: {
      width: '100%',
      borderRadius: 8,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileImage: {
      position: 'relative',
      width: '100%',
      aspectRatio: EXERCISE_VIDEO_CAROUSEL_ASPECT,
    },
    extThumbFill: {
      ...StyleSheet.absoluteFillObject,
    },
    extMainSlideThumb: {
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileCaption: {
      fontSize: 11,
      lineHeight: 14,
      marginTop: 6,
      textAlign: 'center',
      width: '100%',
    },
    extSection: {
      marginTop: 0,
      marginBottom: 12,
      alignSelf: 'stretch',
    },
    extHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
    },
    exerciseVideoCarouselBlock: {
      marginBottom: 12,
      alignSelf: 'stretch',
    },
    exerciseVideoCarouselLoading: {
      marginBottom: 8,
    },
    ytModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    ytModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.55)',
    },
    ytModalSheet: {
      width: '100%',
      maxWidth: 720,
      borderRadius: 12,
      borderWidth: 1,
      overflow: 'hidden',
    },
    ytModalHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingVertical: 12,
      gap: 12,
    },
    ytModalTitle: {
      flex: 1,
      fontSize: 16,
      fontWeight: '600',
    },
    ytModalClose: {
      padding: 4,
    },
    ytModalCloseText: {
      fontSize: 22,
      fontWeight: '700',
      lineHeight: 24,
    },
    ytWebViewWrap: {
      width: '100%',
      aspectRatio: EXERCISE_VIDEO_CAROUSEL_ASPECT,
      backgroundColor: '#000',
    },
    ytWebView: {
      flex: 1,
      width: '100%',
      height: '100%',
    },
    ytModalImage: {
      width: '100%',
      height: '100%',
    },
  };
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: 'transparent',
  },
  breadcrumbContainer: {
    flex: 1,
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
  },
  profileIconContainer: {
    backgroundColor: 'transparent',
  },
  content: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  titleSection: {
    marginBottom: 30,
    alignItems: 'center',
  },
  pageTabRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
    alignSelf: 'stretch',
  },
  pathResetRow: {
    alignItems: 'center',
    marginBottom: 16,
  },
  pathResetButton: {
    padding: 10,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  selectionSectionLocked: {
    opacity: 0.42,
  },
  pathSectionCourse: {
    minHeight: 320,
  },
  pathSectionGrade: {
    minHeight: 200,
  },
  pathSectionNonCourse: {
    minHeight: 140,
  },
  pathSectionCourseTypeSlot: {
    marginTop: 12,
  },
  pathSectionGradeTypeSlot: {
    marginTop: 12,
  },
  pathSectionCourseCreateSlot: {
    marginTop: 0,
  },
  pathSectionSlotInactive: {
    opacity: 0.55,
  },
  pickTriggerInactive: {
    opacity: 0.55,
  },
  pageTab: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
  },
  pageTabOn: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  pageTabText: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.text,
  },
  pageTabTextOn: {
    color: '#000',
  },
  exercisePickList: {
    marginTop: 8,
    gap: 10,
  },
  exerciseListHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 4,
  },
  exerciseListHeaderTextCol: {
    flex: 1,
    maxWidth: '50%',
    minWidth: 0,
  },
  moveExerciseToggleWrap: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    flexShrink: 0,
    maxWidth: '50%',
    gap: 8,
    paddingTop: 2,
  },
  moveExerciseToggleLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
    textAlign: 'right',
  },
  segmentedToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? '#555555' : '#CCCCCC',
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#E8E8E8',
    padding: 3,
    minHeight: 44,
    minWidth: 132,
  },
  segmentedToggleOption: {
    flex: 1,
    minWidth: 60,
    minHeight: 38,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  segmentedToggleOptionActiveOff: {
    backgroundColor: theme.isDarkMode ? '#757575' : '#9E9E9E',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 2,
          elevation: 2,
        }),
  },
  segmentedToggleOptionActiveOn: {
    backgroundColor: '#4CAF50',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.25,
          shadowRadius: 2,
          elevation: 2,
        }),
  },
  segmentedToggleText: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.4,
    color: theme.isDarkMode ? '#CCCCCC' : '#666666',
    backgroundColor: 'transparent',
  },
  segmentedToggleTextActive: {
    color: '#FFFFFF',
  },
  moveExercisePanel: {
    marginTop: 16,
  },
  moveTypeDropdownList: {
    maxHeight: 240,
    marginTop: 4,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
    overflow: 'hidden',
  },
  moveDestDropdownWrap: {
    marginTop: 0,
  },
  moveDestNestedWrap: {
    marginTop: 8,
  },
  moveDestOptionsScroll: {
    maxHeight: 220,
  },
  moveDestEmptyText: {
    padding: 12,
    backgroundColor: 'transparent',
  },
  moveExerciseOrLabel: {
    marginTop: 16,
    marginBottom: 8,
    fontSize: 14,
    fontWeight: '700',
    color: theme.colors.text,
    backgroundColor: 'transparent',
    textAlign: 'center',
  },
  moveDestSectionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
    marginBottom: 8,
  },
  exercisePickCard: {
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
    marginBottom: 10,
    minHeight: 56,
    position: 'relative',
    overflow: 'hidden',
  },
  exercisePickCardMenuOpen: {
    overflow: 'visible',
    zIndex: 8,
  },
  exercisePickCardInner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
  },
  exercisePickCardMain: {
    flex: 1,
    minWidth: 0,
    backgroundColor: 'transparent',
  },
  exercisePickCardSelected: {
    borderColor: '#d4af37',
  },
  exercisePickCardContent: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'transparent',
  },
  exercisePickYellowOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#d4af37',
    opacity: 0.5,
    borderRadius: 10,
  },
  exercisePickNameSelected: {
    color: '#FFFFFF',
  },
  exercisePickDescSelected: {
    color: '#FFFFFF',
    opacity: 0.95,
  },
  exercisePickNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    backgroundColor: 'transparent',
  },
  exercisePickEditIcon: {
    marginRight: 8,
  },
  exercisePickName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.heading || theme.colors.text,
    backgroundColor: 'transparent',
  },
  exercisePickDesc: {
    fontSize: 14,
    lineHeight: 20,
    color: theme.colors.text,
    opacity: 0.9,
    backgroundColor: 'transparent',
  },
  exercisePickDescMuted: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.text,
    opacity: 0.55,
    backgroundColor: 'transparent',
  },
  cardMenuWrap: {
    position: 'relative',
    marginLeft: 8,
    backgroundColor: 'transparent',
  },
  cardMenuButton: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: 'transparent',
  },
  cardDeleteMenu: {
    position: 'absolute',
    top: 38,
    right: 0,
    minWidth: 140,
    backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    zIndex: 20,
    paddingVertical: 4,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.2,
          shadowRadius: 6,
          elevation: 6,
        }),
  },
  cardDeleteMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'transparent',
  },
  cardDeleteMenuItemText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#c62828',
    backgroundColor: 'transparent',
  },
  editFormHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  editFormHintInHeader: {
    flex: 1,
    marginBottom: 0,
  },
  editFormDeleteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    backgroundColor: 'transparent',
  },
  editFormDeleteBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#c62828',
    backgroundColor: 'transparent',
  },
  editFormHint: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.85,
    marginBottom: 16,
  },
  editUpdateToast: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginTop: 8,
    marginBottom: 18,
  },
  editUpdateToastSuccess: {
    backgroundColor: '#2e7d32',
  },
  editUpdateToastError: {
    backgroundColor: '#c62828',
  },
  editUpdateToastMessage: {
    flex: 1,
    color: '#ffffff',
    fontSize: 15,
    lineHeight: 20,
    marginRight: 8,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  editUpdateToastClose: {
    padding: 2,
    marginTop: -2,
    backgroundColor: 'transparent',
  },
  existingImagesBlock: {
    marginBottom: 10,
  },
  existingImageRemoveHit: {
    minWidth: 36,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
    backgroundColor: theme.isDarkMode ? 'rgba(0,0,0,0.55)' : theme.colors.primary || '#d4af37',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: theme.isDarkMode ? '#FDFD0A' : '#000000',
    marginBottom: 10,
    textAlign: 'center',
    letterSpacing: 1,
    ...(Platform.OS === 'web' ? {
      fontFamily: 'serif, Georgia, "Times New Roman", Times, serif',
      textTransform: 'uppercase',
    } : {
      textTransform: 'uppercase',
    }),
  },
  subtitle: {
    fontSize: 14,
    fontStyle: 'italic',
    color: theme.colors.text,
    textAlign: 'center',
    paddingHorizontal: 20,
    lineHeight: 20,
    opacity: 0.8,
  },
  form: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
  fieldContainer: {
    marginBottom: 20,
  },
  youtubeOAuthBar: {
    marginBottom: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 8,
  },
  helpText: {
    fontSize: 12,
    color: theme.colors.text,
    opacity: 0.7,
    marginBottom: 8,
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  creationAccessHint: {
    fontSize: 12,
    color: theme.colors.text,
    opacity: 0.75,
    marginBottom: 8,
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  input: {
    backgroundColor: theme.colors.inputBackground,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.colors.text,
    borderWidth: 1,
    borderColor: theme.colors.inputBorder,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  sequenceCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  exerciseTrackingOptions: {
    gap: 8,
    backgroundColor: 'transparent',
  },
  sequenceCheckboxMark: {
    fontSize: 22,
    marginRight: 10,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  sequenceCheckboxLabel: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: '500',
    backgroundColor: 'transparent',
  },
  sequenceHintText: {
    fontSize: 13,
    fontStyle: 'italic',
    color: theme.isDarkMode ? '#FDFD0A' : theme.colors.primary || '#d4af37',
    marginTop: 6,
    marginBottom: 4,
    lineHeight: 18,
    backgroundColor: 'transparent',
  },
  exerciseOrderSection: {
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  exerciseOrderTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.heading || theme.colors.text,
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  exerciseOrderLoading: {
    marginVertical: 12,
  },
  exerciseOrderList: {
    maxHeight: 220,
    marginTop: 8,
  },
  exerciseOrderItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.isDarkMode ? '#FDFD0A' : theme.colors.primary || '#0070D2',
  },
  exerciseOrderControls: {
    flexDirection: 'row',
    marginRight: 12,
    backgroundColor: 'transparent',
  },
  orderButton: {
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
    borderRadius: 4,
    marginRight: 4,
  },
  orderButtonDisabled: {
    opacity: 0.3,
  },
  orderButtonText: {
    fontSize: 16,
    color: theme.colors.text,
    fontWeight: 'bold',
    backgroundColor: 'transparent',
  },
  exerciseOrderItemName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  uploadButton: {
    ...(theme.isDarkMode
      ? {
          backgroundColor: theme.colors.primary || '#d4af37',
          borderWidth: 0,
        }
      : {
          backgroundColor: theme.colors.surface,
          borderWidth: 2,
          borderColor: theme.colors.primary || '#d4af37',
        }),
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  uploadButtonText: {
    color: theme.isDarkMode ? '#000000' : theme.colors.primary || '#d4af37',
    fontSize: 16,
    fontWeight: '600',
  },
  webVideoPickButton: {
    position: 'relative',
    overflow: 'hidden',
  },
  readOnlyVideoInput: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text,
    marginRight: 10,
    paddingVertical: 8,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  videoPickError: {
    marginTop: 6,
    fontSize: 13,
    color: theme.isDarkMode ? '#e57373' : '#c62828',
    backgroundColor: 'transparent',
  },
  browseFilesButton: {
    marginTop: 10,
    alignSelf: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.primary || '#d4af37',
    backgroundColor: 'transparent',
  },
  browseFilesButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.primary || '#d4af37',
    backgroundColor: 'transparent',
  },
  pendingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
  },
  pendingName: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text,
    marginRight: 10,
  },
  pendingRemoveHit: {
    minWidth: 36,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 18,
    borderWidth: theme.isDarkMode ? 0 : 1,
    borderColor: theme.isDarkMode ? 'transparent' : theme.colors.border,
    backgroundColor: theme.isDarkMode ? 'rgba(0,0,0,0.55)' : theme.colors.primary || '#d4af37',
  },
  pendingRemove: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '700',
  },
  toggleRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  toggleChip: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
  },
  toggleChipOn: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  toggleChipText: {
    color: theme.colors.text,
    fontWeight: '600',
    fontSize: 13,
  },
  toggleChipTextOn: {
    color: '#000',
  },
  helpTextEmphasis: {
    fontWeight: '600',
    color: theme.colors.text,
  },
  pendingFileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    paddingVertical: 4,
    backgroundColor: 'transparent',
  },
  pendingFileName: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.text,
    marginRight: 10,
    backgroundColor: 'transparent',
  },
  pendingFileRemove: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.isDarkMode ? '#e57373' : '#757575',
    paddingHorizontal: 4,
    backgroundColor: 'transparent',
  },
  assignStudentsBlock: {
    marginTop: 16,
  },
  assignStudentsTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    backgroundColor: 'transparent',
  },
  assignStudentsLabel: {
    marginBottom: 0,
    flexShrink: 1,
    backgroundColor: 'transparent',
  },
  assignSelectAllControl: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'transparent',
  },
  assignSelectAllLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  assignSelectAllCheckbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderColor: theme.isDarkMode ? '#FFFFFF' : '#333333',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  assignSelectAllCheckboxSelected: {
    backgroundColor: '#d4af37',
    borderColor: '#d4af37',
  },
  assignSelectAllCheckmark: {
    color: theme.isDarkMode ? '#000000' : '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
    backgroundColor: 'transparent',
  },
  assignStudentsLoading: {
    marginTop: 8,
  },
  pickDropdownWrap: {
    marginTop: 0,
  },
  pickTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  pickTriggerText: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  pickTriggerPlaceholder: {
    opacity: 0.65,
  },
  pickTriggerDisabled: {
    opacity: 0.7,
  },
  pickTriggerArrow: {
    fontSize: 14,
    color: theme.colors.text,
    marginLeft: 8,
    backgroundColor: 'transparent',
  },
  pickDropdownList: {
    maxHeight: 280,
    marginTop: 4,
  },
  assignStudentsList: {
    maxHeight: 280,
    marginTop: 4,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
  },
  assignStudentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
    backgroundColor: 'transparent',
  },
  assignStudentRowLocked: {
    opacity: 0.92,
  },
  assignStudentRowDisabled: {
    opacity: 0.45,
  },
  assignMyExerciseOnlyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginBottom: 4,
    borderBottomWidth: 3,
    borderBottomColor: theme.colors.primary || '#d4af37',
    backgroundColor: 'transparent',
  },
  assignMyExerciseOnlyRowSelected: {
    backgroundColor: theme.isDarkMode ? 'rgba(212, 175, 55, 0.12)' : 'rgba(212, 175, 55, 0.08)',
  },
  assignMyExerciseOnlyLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  assignStudentCheckboxMark: {
    width: 28,
    fontSize: 22,
    lineHeight: 24,
    textAlign: 'center',
    color: theme.colors.primary || '#d4af37',
    backgroundColor: 'transparent',
    marginRight: 12,
  },
  assignStudentCheckboxMarkSelected: {
    color: theme.colors.primary || '#d4af37',
  },
  assignStudentName: {
    flex: 1,
    fontSize: 15,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownContainer: {
    marginTop: 8,
  },
  dropdownOption: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  dropdownOptionSelected: {
    ...(theme.isDarkMode
      ? {
          backgroundColor: theme.colors.primary || '#d4af37',
          borderColor: theme.colors.primary || '#d4af37',
        }
      : {
          backgroundColor: 'rgba(212, 175, 55, 0.14)',
          borderColor: theme.colors.primary || '#d4af37',
          borderWidth: 2,
        }),
  },
  dropdownOptionDisabled: {
    opacity: 0.45,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#F5F5F5',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.colors.text,
    backgroundColor: 'transparent',
  },
  dropdownOptionTextSelected: {
    color: theme.isDarkMode ? '#FFFFFF' : theme.colors.text,
    fontWeight: '600',
    backgroundColor: 'transparent',
  },
  dropdownOptionTextDisabled: {
    color: theme.isDarkMode ? '#888' : '#999',
    backgroundColor: 'transparent',
  },
  createNewContainer: {
    marginTop: 15,
    padding: 15,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.background : theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderStyle: 'dashed',
  },
  createNewText: {
    fontSize: 14,
    color: theme.colors.text,
    marginBottom: 10,
    textAlign: 'center',
    fontStyle: 'italic',
    backgroundColor: 'transparent',
  },
  createNewButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  createNewButtonRed: {
    backgroundColor: '#DC3545',
  },
  createNewButtonDisabled: {
    opacity: 0.45,
  },
  createNewButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
    gap: 15,
  },
  button: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  resetButton: {
    backgroundColor: '#999',
  },
  saveButton: {
    backgroundColor: theme.colors.primary || '#d4af37',
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  // Status Box Styles
  statusBox: {
    width: '100%',
    padding: 30,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    } : {
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  successBox: {
    backgroundColor: '#d4af37', // Yellow
  },
  errorBox: {
    backgroundColor: '#DC3545', // Red
  },
  statusBoxTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    backgroundColor: 'transparent',
  },
  successBoxText: {
    color: '#000000', // Black text on yellow background
  },
  errorBoxText: {
    color: '#FFFFFF', // White text on red background
  },
  errorMessage: {
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 20,
    paddingHorizontal: 10,
    backgroundColor: 'transparent',
  },
  statusButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 20,
    gap: 15,
  },
  statusButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  backButton: {
    backgroundColor: '#000000',
  },
  resetStatusButton: {
    backgroundColor: '#333333',
  },
  statusButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});
